/**
 * 보고 문서에 싣는 표 — 합계 요약표와 예외 행. 원자료 앞 몇 행은 싣지 않는다.
 *
 * 국가직 팀장 검토(2026-10-23): 「상사에게 무작위 표본 10행은 아무 의미가 없다. 보여 줄 행은 예외 건이다.」
 * 예정일 10.1. 인데 아직 「계획」인 중앙역 2건이 앞 10행의 맨 아래에 묻혀 있었다.
 */
import { capTable } from './files';
import type { TableData } from './files';
import type { DataReading } from './reading';
import { topicParticle, withParticle } from './korean';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
/** 주의할 상태값. 「계획」은 「완료」 곁에 있을 때만 아직 안 한 것이다. */
const ATTENTION = /미실시|미처리|미수|미결|미완료|지연|초과|누락|부적합|미납|미입고/;
const FEW = 12;
/** 〈표 2〉에 싣는 이름(현장) 수. 나머지는 「외 N건」. */
const MAX_EXCEPTIONS = 7;
/**
 * 경과를 둘로 나눈 〈표 2〉의 이름 줄. 오래 지난 묶음은 여섯(하나 남으면 그 이름까지 일곱)이다. 쏠림 줄의 「가나본부 5개 현장」 중
 * 서중앙역이 「그 밖」에 묻혔다(9차 검토). 최근 예정분 묶음은 소계 한 줄로 접는다. 9·10월 두 달 판에서 PDF가 두 쪽이
 * 됐다(2026-10-25). 그래서 표는 소계 둘·계까지 10줄 안이고 발표자료 표(10줄)에도 그대로 든다.
 */
const SPLIT_OLDER = MAX_EXCEPTIONS - 1;
const FULL_DATE = /^((?:19|20)\d{2})-?(0[1-9]|1[0-2])-?(0[1-9]|[12]\d|3[01])$/;
const MONTH = /^((?:19|20)\d{2})-?(0[1-9]|1[0-2])$/;
const OWNER = /담당|사원|성명|작성자|처리자|직원/;
/** 호기 칸 — 현장명과 함께 호기를 가린다. 같은 호기가 두 달에 한 번씩 세어져 103건이 실은 65호기였다(10차 검토). */
const UNIT = /호기|설비|장비|기기/;

/**
 * 「'26. 10. 23.(금) 16:58 기준」. 조회 시각까지 — 같은 날 2,349건이 2,348건으로 바뀌었다(국가직 팀장 2차 검토).
 * 시각이 없는 날짜(자정)면 날짜만.
 */
export function basisLabel(date: Date): string {
  const year = String(date.getFullYear()).slice(2);
  const time = date.getHours() || date.getMinutes()
    ? ` ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}` : '';
  return `'${year}. ${date.getMonth() + 1}. ${date.getDate()}.(${WEEKDAYS[date.getDay()]})${time} 기준`;
}

/** 20261001 → '26.10.1. · 202608 → '26.8월. 날짜가 아니면 그대로. */
export function shortDate(value: string): string {
  const text = value.trim();
  const full = text.match(FULL_DATE);
  if (full) return `'${full[1]!.slice(2)}.${Number(full[2])}.${Number(full[3])}.`;
  const month = text.match(MONTH);
  if (month) return `'${month[1]!.slice(2)}.${Number(month[2])}월`;
  return value;
}

function text(cell: unknown): string {
  return cell === null || cell === undefined ? '' : String(cell).trim();
}

/**
 * 달 값 — 202609 · 2026-09 · 2026.09 · 「2026년 9월」 → '202609'. 아니면 ''. 1턴 계산이 판마다 달 칸을 다른 꼴로 써서
 * 「2026년 9월」 판을 여러 달 판으로 못 알아봤다(2026-10-25 09:03).
 */
export function monthKey(value: string): string {
  const plain = value.trim().match(/^((?:19|20)\d{2})[-./]?(0[1-9]|1[0-2])$/);
  if (plain) return `${plain[1]}${plain[2]}`;
  const korean = value.trim().match(/^((?:19|20)\d{2})\s*년\s*(1[0-2]|0?[1-9])\s*월$/);
  return korean ? `${korean[1]}${(korean[2] ?? '').padStart(2, '0')}` : '';
}

/** 달 칸 — 값이 모두 달(monthKey)인 칸. */
export function monthColumn(table: TableData, skip = -1): number {
  return table.columns.findIndex((_, index) => index !== skip && table.rows.length > 0
    && table.rows.every((row) => monthKey(text(row[index]))));
}

/** 보고 문서의 말 — 상태 코드 「계획」은 「완료」 곁에서 「미실시」다. */
/**
 * 「계획」은 「완료」 곁에 있거나, 목록 전부가 「계획」일 때(아직 안 한 건만 뽑은 목록) 미실시다.
 * 461건 전부 「계획」인 미실시 목록을 상태 열로 못 봐 경과·예정을 못 나눴다(국가직 팀장 2차 검토, 2026-10-23).
 */
function plainStatus(value: string, values: string[]): string {
  return value === '계획' && (values.indexOf('완료') >= 0 || values.length === 1) ? '미실시' : value;
}

type Status = { index: number; values: string[]; attention: string };

/**
 * ※ 판정 줄: 상태 코드를 문서의 말로 바꾼 것이 있으면 「결과 「계획」은 미실시」. 산문 속 「=」와 「 · 」 나열은 쓰지 않는다
 * (사용자 전역 지침 stop-slop-ko, 11차 검토 때 스캐너가 ※ 판정 줄을 걸렀다).
 */
export function statusRuleOf(table: TableData): string | undefined {
  const status = statusColumn(table);
  if (!status) return undefined;
  const renamed = status.values.filter((value) => plainStatus(value, status.values) !== value);
  return renamed.length
    ? renamed.map((value) => `${table.columns[status.index]} 「${value}」${topicParticle(value).slice(value.length)}`
      + ` ${plainStatus(value, status.values)}`).join(', ')
    : undefined;
}

/** 상태 열의 값을 보고 문서의 말로(「계획」→「미실시」). */
export function statusName(status: Status, value: unknown): string {
  return plainStatus(text(value), status.values);
}

/**
 * 상태 열: 가짓수가 적고 주의 상태값이 든 열. 자료 읽기가 열린 문제(결함, 고장)로 읽은 표에는 없다고 한다: 그 표의 「미처리」는
 * 할 일을 안 한 것이 아니라 찾은 문제가 남은 것이다. 경과, 두 달 묶음, 〈표 1〉·〈표 2〉, 메모, 서명·미전송이 모두 이 칸을 요구해
 * 한꺼번에 꺼진다. 중대결함 104건이 「예정일 경과 미실시 101건」, 조치 「점검 완료」로 나갔다(실제 테넌트, 2026-10-27).
 */
export function statusColumn(table: TableData): Status | undefined {
  if (table.reading?.story === 'issues') return undefined;
  for (let index = 0; index < table.columns.length; index += 1) {
    const values = Array.from(new Set(table.rows.map((row) => text(row[index])).filter(Boolean)));
    if (!values.length || values.length > FEW) continue;
    const named = values.map((value) => plainStatus(value, values));
    const attention = named.find((value) => ATTENTION.test(value));
    if (attention) return { index, values, attention };
  }
  return undefined;
}

/** 합계 요약표: 구분 · 전체 · 상태별 건수 · 주의 상태 비율. 달 열이 있으면 달마다 한 줄. */
export function statusTable(table: TableData): { table: TableData; attention: string } | undefined {
  const status = statusColumn(table);
  if (!status) return undefined;
  const named = (value: string) => plainStatus(value, status.values);
  const counts = new Map<string, number>();
  table.rows.forEach((row) => {
    const value = text(row[status.index]);
    if (value) counts.set(named(value), (counts.get(named(value)) ?? 0) + 1);
  });
  // 상태가 하나뿐인 목록(전부 미실시)으로 요약표를 만들면 「미실시 100%」가 된다 — 전체를 모르는 것이다.
  if (counts.size < 2) return undefined;
  // 주의 상태는 맨 끝, 나머지는 많은 순.
  const order = Array.from(counts.keys())
    .sort((left, right) => (left === status.attention ? 1 : right === status.attention ? -1
      : (counts.get(right) ?? 0) - (counts.get(left) ?? 0)));
  const monthIndex = monthColumn(table, status.index);
  const groups = monthIndex >= 0
    ? Array.from(new Set(table.rows.map((row) => monthKey(text(row[monthIndex]))))).sort()
    : ['합계'];
  const rows = groups.map((group) => {
    const members = monthIndex >= 0 ? table.rows.filter((row) => monthKey(text(row[monthIndex])) === group) : table.rows;
    const each = order.map((name) => members.filter((row) => named(text(row[status.index])) === name).length);
    const total = each.reduce((sum, one) => sum + one, 0);
    const watched = each[order.length - 1] ?? 0;
    const share = total ? Math.round((watched / total) * 1000) / 10 : 0;
    return [monthIndex >= 0 ? shortDate(group) : group, total, ...each, share];
  });
  return {
    table: { columns: ['구분', '전체', ...order, `${status.attention} 비율(%)`], rows },
    attention: status.attention,
  };
}

/**
 * 그림의 주의 칸이 원자료 주의 건의 일부(지연 60 ⊂ 미실시 396)면 합이 그 수가 되는 칸들 — 이름만 바꾸면 「미실시 60」이
 * 됐다(실제 테넌트, 2026-10-25). 주의 칸이 그 수 이상이면(원자료가 경과 건만 담은 판) 그 칸 하나다.
 * 0 칸은 묶음을 가르지 않는다. 묶음이 하나로 정해지지 않으면 undefined.
 */
function attentionGroup(sums: number[], at: number, count: number): number[] | undefined {
  if ((sums[at] ?? 0) >= count) return [at];
  const others = sums.map((_, index) => index).filter((index) => index !== at && sums[index]);
  if (others.length > 8) return undefined;
  const groups: number[][] = [];
  for (let mask = 1; mask < 1 << others.length; mask += 1) {
    const members = others.filter((_, bit) => mask & (1 << bit));
    // 하나는 남아야 한다 — 전부 묶으면 주의 비율이 100%다.
    if (members.length < others.length
      && members.reduce((sum, index) => sum + (sums[index] ?? 0), sums[at] ?? 0) === count) groups.push([at, ...members]);
  }
  return groups.length === 1 ? groups[0] : undefined;
}

/**
 * 계산 요약의 구성 막대(완료·미실시)로 합계 요약표 — 표가 경과 건만 담아 요약표를 못 만든 판(실제 테넌트, 2026-10-23).
 * 숫자는 계산 요약의 값이다 — 답 글에서 긁지 않는다.
 */
export function statusFromChart(
  chart: { kind?: string; categories: string[]; series: { name?: string; values: (number | null)[] }[] } | undefined,
  /** 원자료 표의 주의 상태 말(「미실시」). 주면 칸 이름을 그것으로 — 「미완료」 칸 아래에 「미실시」 줄이 붙었다(2026-10-24). */
  word?: string,
  /** 원자료 표의 주의 건수. 주면 그림의 주의 칸과 맞춰 보고, 일부면 합이 맞는 칸들을 묶는다. 안 맞으면 그림의 이름 그대로. */
  count?: number,
): { table: TableData; attention: string } | undefined {
  if (!chart) return undefined;
  // 달별 그림(범주 '26.9월, 계열 완료·미실시)이면 달마다 한 줄 — 〈표 1〉이 빠졌다(실제 테넌트, 2026-10-24).
  if (chart.kind !== 'share') {
    const names = chart.series.map((series) => series.name ?? '');
    const found = names.find((name) => ATTENTION.test(name));
    if (!found || names.length < 2 || !chart.categories.every((category) => /^'\d{2}\.\d{1,2}월$/.test(category))) return undefined;
    const at = names.indexOf(found);
    const sums = chart.series.map((series) => series.values.reduce<number>((sum, value) => sum + (value ?? 0), 0));
    const group = word && count !== undefined ? attentionGroup(sums, at, count) : [at];
    const attention = group && word ? word : found;
    const merged = group ?? [at];
    const order = [...names.map((_, index) => index).filter((index) => merged.indexOf(index) < 0), at];
    const rows = chart.categories.map((category, column) => {
      const each = order.map((index) => (index === at ? merged : [index])
        .reduce((sum, one) => sum + (chart.series[one]?.values[column] ?? 0), 0));
      const total = each.reduce((sum, value) => sum + value, 0);
      return [category, total, ...each, total ? Math.round(((each[each.length - 1] ?? 0) / total) * 1000) / 10 : 0];
    });
    return {
      table: { columns: ['구분', '전체', ...order.map((index) => (index === at ? attention : names[index] ?? '')), `${attention} 비율(%)`], rows },
      attention,
    };
  }
  const found = chart.categories.find((category) => ATTENTION.test(category));
  const values = (chart.series[0]?.values ?? []).map((value) => value ?? 0);
  const total = values.reduce((sum, value) => sum + value, 0);
  if (!found || !total) return undefined;
  const at = chart.categories.indexOf(found);
  const group = word && count !== undefined ? attentionGroup(values, at, count) : [at];
  const attention = group && word ? word : found;
  const merged = group ?? [at];
  const watched = merged.reduce((sum, index) => sum + (values[index] ?? 0), 0);
  const kept = chart.categories.map((_, index) => index).filter((index) => index === at || merged.indexOf(index) < 0);
  return {
    table: {
      columns: ['구분', '전체', ...kept.map((index) => (index === at ? attention : chart.categories[index] ?? '')), `${attention} 비율(%)`],
      rows: [['합계', total, ...kept.map((index) => (index === at ? watched : values[index] ?? 0)), Math.round((watched / total) * 1000) / 10]],
    },
    attention,
  };
}

/** 하루를 수로 — 20261023. */
function dayNumber(date: Date): number {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

/** 날짜 하나(「'26.10.27.」). */
export function dayLabel(date: Date): string {
  return shortDate(String(dayNumber(date)));
}

/** 달력에 있는 날이면 그 수(20261023), 없는 날(20260931)이면 0 — 없는 날로는 경과를 셀 수 없다(10차 검토). 그림도 이것으로 센다. */
export function calendarDay(value: string): number {
  const full = value.trim().match(FULL_DATE);
  if (!full) return 0;
  const [year, month, day] = [Number(full[1]), Number(full[2]), Number(full[3])];
  const date = new Date(year, month - 1, day);
  return date.getMonth() === month - 1 && date.getDate() === day ? year * 10000 + month * 100 + day : 0;
}

/**
 * 날짜 칸 — 값이 날짜 꼴(20261001)이거나 빈 칸이고, 날짜가 하나는 있는 칸. 모두 빈 칸인 칸은 아니다 — 기준월 행만 남긴
 * 표에서 비어 있던 비고 칸이 날짜 칸이 됐다(2026-10-25 코드 리뷰). 그림도 이것으로 고른다.
 */
export function dateColumn(table: TableData): number {
  return table.columns.findIndex((_, index) => table.rows.some((row) => FULL_DATE.test(text(row[index])))
    && table.rows.every((row) => !text(row[index]) || FULL_DATE.test(text(row[index]))));
}

/** 이름 열 — 현장·거래처·부서처럼 무엇인지 가리키는 글자 열. 없으면 첫 글자 열. */
function nameColumn(table: TableData, skip: number[]): number {
  const candidates = table.columns.map((_, index) => index).filter((index) => skip.indexOf(index) < 0
    && table.rows.some((row) => text(row[index])) && !table.rows.every((row) => /^[\d,.\-]*$/.test(text(row[index]))));
  return candidates.find((index) => /현장|거래처|고객|사업장|부서|업체|매장|지점/.test(table.columns[index] ?? ''))
    ?? candidates[0] ?? -1;
}

function counted(rows: unknown[][], index: number): [string, number][] {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const key = text(row[index]);
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
}

export type DueSplit = {
  attention: string; total: number; overdue: number; today: number; upcoming: number;
  lastUpcoming?: string; siteLabel: string; bySite: [string, number][]; byOwner: [string, number][];
  /** 경과 중 직전 근무일 예정분 — 토요일 새벽 조회에 금요일 예정분은 아직 입력 전일 수 있다(6차 검토). */
  recent: number; recentDay?: string; olderLast?: string;
  /** 직전 근무일 뒤(주말) 예정분까지 묶었으면 그 마지막 날 — 일요일 조회에 토요일 예정분이 「금요일 예정」으로 읽혔다(2026-10-25). */
  recentLast?: string;
  /** 오래 지난 경과분이 기준일보다 며칠 앞인지(가장 늦은 예정일 기준), 그리고 두 묶음의 담당별 건수. */
  olderDays?: number; olderOwners: [string, number][]; recentOwners: [string, number][];
  /** 오래 지난 경과분의 가장 이른 예정일과 그 날 수 — 「11~24일 경과」. 가장 오래된 건이 24일 지났다는 것이 핵심이다(8차 검토). */
  olderFirst?: string; olderMaxDays?: number;
  /** 오래 지난 경과분의 이름(현장)별 건수·담당 — 쏠림 줄의 분모가 60(전체 경과)과 52(조치)로 섞였다(8차 검토). */
  olderBySite: [string, number][]; olderSiteOwners: [string, string[]][];
  /** 배정분을 하나도 못 한 담당: [이름, 배정, 경과]. 완료 행이 있는 표에서만 센다(8차 검토: A 배정 49건 중 완료 0건). */
  idleOwners: [string, number, number][];
  /** 날짜가 달력에 없는 주의 건(20260931) — 경과·예정 어디에도 넣지 않는다(10차 검토). */
  invalid: number;
  /** 경과 건의 이름(현장)별 담당. */
  siteOwners: [string, string[]][];
};

/** 기준일 앞의 마지막 근무일. 다음 근무일과 같은 규칙으로 양력 공휴일도 건너뛴다(10월 12일(월)의 직전 근무일은 10월 8일). */
function previousWorkday(basis: Date): Date {
  const day = new Date(basis.getFullYear(), basis.getMonth(), basis.getDate() - 1);
  while (!workday(day)) day.setDate(day.getDate() - 1);
  return day;
}

/** 기준일 뒤의 첫 평일 — 직전 근무일분을 확인할 기한. */
/**
 * 양력 공휴일(신정, 삼일절, 어린이날, 현충일, 광복절, 개천절, 한글날, 성탄절). 12월 31일에 「다음 근무일 '27.1.1.」(신정)을 기한
 * 후보로 주었다(독립 재검토). 음력 공휴일(설날, 추석)과 대체 공휴일은 해마다 달라 아직 모른다.
 */
const SOLAR_HOLIDAYS = new Set(['1-1', '3-1', '5-5', '6-6', '8-15', '10-3', '10-9', '12-25']);

function workday(day: Date): boolean {
  return day.getDay() !== 0 && day.getDay() !== 6 && !SOLAR_HOLIDAYS.has(`${day.getMonth() + 1}-${day.getDate()}`);
}

function nextWorkday(basis: Date): Date {
  const day = new Date(basis.getFullYear(), basis.getMonth(), basis.getDate() + 1);
  while (!workday(day)) day.setDate(day.getDate() + 1);
  return day;
}

/** 그 달의 마지막 근무일(토·일과 양력 공휴일을 앞으로 당긴다). */
function lastWorkday(year: number, month: number): Date {
  const day = new Date(year, month + 1, 0);
  while (!workday(day)) day.setDate(day.getDate() - 1);
  return day;
}

/**
 * 점검이 아닌 문서(목록 보고·공지)의 기한 후보. 근거에 없는 날짜는 지어낸 숫자로 버려서, 조치와 요청에 기한을 달 수 없었다
 * (실측 2026-10-26). 올해 말일은 연차처럼 해가 끝나면 사라지는 것의 사용 기한이다. 기준일보다 뒤 날짜만 준다(토요일
 * 10월 31일에 「이번 달 마지막 근무일 10월 30일」을 주었다, 독립 검토).
 */
export function deadlineFacts(basis: Date, options: { notice?: boolean } = {}): string {
  // 공지(직원에게 주는 기한)는 사흘 안의 날짜를 주지 않는다. 공지를 붙인 다음 근무일까지 사용 계획을 내라는 요청이 나갔다
  // (실측 2026-10-26 17:08). 보고 문서의 조치(관리자가 지시할 일)는 다음 근무일부터 준다.
  // 올해 말일은 사용 기한이라 연말 사흘 동안도 남긴다(독립 재검토: 12월 29일 공지의 후보가 다음 달 마지막 근무일 하나뿐이었다).
  // 보고 문서에는 다음 주 마지막 근무일도 준다. 부서장의 계획 취합 기한이 직원의 제출 기한(이번 달 말)과 같은 날이었다(국가직 팀장 재검토).
  const day = (offset: number) => new Date(basis.getFullYear(), basis.getMonth(), basis.getDate() + offset);
  const monday = day(-((basis.getDay() + 6) % 7));
  const candidates: [string, Date][] = [
    ['다음 근무일', nextWorkday(basis)],
    ['이번 달 마지막 근무일', lastWorkday(basis.getFullYear(), basis.getMonth())],
    ...(options.notice ? [] : [['다음 주 마지막 근무일', previousWorkday(
      new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 12))] as [string, Date]]),
    ['다음 달 마지막 근무일', lastWorkday(basis.getFullYear(), basis.getMonth() + 1)],
    ['올해 말일', new Date(basis.getFullYear(), 11, 31)],
  ];
  const usable = candidates.filter(([name, date]) => (
    dayNumber(date) >= dayNumber(day(options.notice && name !== '올해 말일' ? 3 : 1))))
    .sort((left, right) => dayNumber(left[1]) - dayNumber(right[1]));
  const kept = usable.filter(([, date], at) => usable.findIndex(([, other]) => dayNumber(other) === dayNumber(date)) === at);
  return `기한 후보: ${kept.map(([name, date]) => `${name} ${dayLabel(date)}`).join(', ')}`;
}

/** 다음 주 마지막 근무일(기한 후보의 그 날). 연차 촉진의 「지금 챙길 직원」 범위의 끝이다. */
export function nextWeekEnd(basis: Date): Date {
  const monday = new Date(basis.getFullYear(), basis.getMonth(), basis.getDate() - ((basis.getDay() + 6) % 7));
  return previousWorkday(new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 12));
}

/**
 * 코드가 쓰는 조치의 기한: 다음 근무일, 올해 말일, 연말 뒤 결과 보고일(다음 해 1월 8일까지의 마지막 근무일, 「'26.10.27.」 꼴).
 * 결과 취합과 보고가 사용 권장 기한과 같은 날이면 결과를 모을 수 없다(국가직 팀장 검토).
 */
export function actionDays(basis: Date): { next: string; yearEnd: string; report: string } {
  return {
    next: dayLabel(nextWorkday(basis)), yearEnd: dayLabel(new Date(basis.getFullYear(), 11, 31)),
    report: dayLabel(previousWorkday(new Date(basis.getFullYear() + 1, 0, 9))),
  };
}

/**
 * 주의 상태 건을 기준일로 나눈다 — 예정일 경과 · 당일 · 남은 예정. 날짜 열이 없으면 나누지 않는다(지났는지 모른다).
 * 1턴 계산이 나누지 못해도 표에 날짜가 있으면 코드가 나눈다 — 「미집계」라고 쓰면 일을 안 한 것으로 본다.
 */
export function dueSplit(table: TableData, basis: Date): DueSplit | undefined {
  const status = statusColumn(table);
  if (!status) return undefined;
  const dateIndex = dateColumn(table);
  if (dateIndex < 0) return undefined;
  const flagged = table.rows.filter((row) => plainStatus(text(row[status.index]), status.values) === status.attention);
  if (!flagged.length) return undefined;
  const today = dayNumber(basis);
  const day = (row: unknown[]) => calendarDay(text(row[dateIndex]));
  const overdue = flagged.filter((row) => day(row) && day(row) < today);
  const upcoming = flagged.filter((row) => day(row) > today);
  const owner = table.columns.findIndex((column) => OWNER.test(column));
  const site = nameColumn(table, [status.index, dateIndex, owner]);
  const last = upcoming.reduce((most, row) => Math.max(most, day(row)), 0);
  const workday = dayNumber(previousWorkday(basis));
  const recent = overdue.filter((row) => day(row) >= workday);
  // 최근 예정분의 날짜는 남은 건의 실제 예정일로 쓴다. 직전 근무일분이 다 입력되고 다음 날 1건만 남았는데
  // 「'26.10.23.~'26.10.24. 예정 1건」으로 써서 〈표 2〉 「'26.10.24. 예정분」과 어긋났다(실측 12:46).
  const recentFirst = recent.reduce((least, row) => Math.min(least, day(row)), Number.MAX_SAFE_INTEGER);
  const recentLast = recent.reduce((most, row) => Math.max(most, day(row)), 0);
  const older = overdue.filter((row) => day(row) < workday);
  const olderLast = older.reduce((most, row) => Math.max(most, day(row)), 0);
  const olderFirst = older.reduce((least, row) => Math.min(least, day(row)), olderLast);
  const asDate = (value: number) => new Date(Math.floor(value / 10000), Math.floor(value / 100) % 100 - 1, value % 100);
  const since = (value: number) => Math.round((asDate(today).getTime() - asDate(value).getTime()) / 86400000);
  const siteOwners = (rows: unknown[][]): [string, string[]][] => {
    const owners = new Map<string, string[]>();
    if (site >= 0 && owner >= 0) {
      rows.forEach((row) => {
        const list = owners.get(text(row[site])) ?? [];
        if (list.indexOf(text(row[owner])) < 0) list.push(text(row[owner]));
        owners.set(text(row[site]), list);
      });
    }
    return Array.from(owners.entries());
  };
  // 배정분을 하나도 못 한 담당 — 완료 행이 있는 표에서만(주의 건만 담은 목록이면 누구나 완료 0건이다).
  const finished = (row: unknown[]) => !!text(row[status.index])
    && plainStatus(text(row[status.index]), status.values) !== status.attention;
  const idle = owner >= 0 && table.rows.some(finished)
    ? counted(overdue, owner).flatMap(([name, late]): [string, number, number][] => {
      const assigned = table.rows.filter((row) => text(row[owner]) === name);
      return late >= 2 && !assigned.some(finished) ? [[name, assigned.length, late]] : [];
    }) : [];
  return {
    recent: recent.length,
    ...(recent.length ? { recentDay: shortDate(String(recentFirst)) } : {}),
    ...(recent.length && recentLast > recentFirst ? { recentLast: shortDate(String(recentLast)) } : {}),
    ...(olderLast ? {
      olderLast: shortDate(String(olderLast)), olderDays: since(olderLast),
      olderFirst: shortDate(String(olderFirst)), olderMaxDays: since(olderFirst),
    } : {}),
    olderOwners: owner >= 0 ? counted(older, owner) : [],
    recentOwners: owner >= 0 ? counted(recent, owner) : [],
    olderBySite: site >= 0 ? counted(older, site) : [],
    olderSiteOwners: siteOwners(older),
    idleOwners: idle,
    invalid: flagged.filter((row) => text(row[dateIndex]) && !day(row)).length,
    siteOwners: siteOwners(overdue),
    attention: status.attention,
    total: flagged.length,
    overdue: overdue.length,
    today: flagged.filter((row) => day(row) === today).length,
    upcoming: upcoming.length,
    ...(last ? { lastUpcoming: shortDate(String(last)) } : {}),
    siteLabel: site >= 0 ? (table.columns[site] ?? '').replace(/명$/, '') : '',
    bySite: site >= 0 ? counted(overdue, site) : [],
    byOwner: owner >= 0 ? counted(overdue, owner) : [],
  };
}

/**
 * 달 칸과 기준월 — 두 달 이상을 조회한 표에서 진행 중인 달(기준월)과 마감된 달을 가른다(10차 검토). 오늘이 속한 달이
 * 자료에 없으면 마지막 달이 기준월이고 current 는 거짓 — 모든 달이 마감이다(「10월은 2일 현재」라고 썼다, 코드 리뷰).
 */
export type MonthScope = { index: number; ref: string; closed: string[]; current: boolean };

export function monthScope(table: TableData, basis: Date): MonthScope | undefined {
  const index = monthColumn(table);
  if (index < 0) return undefined;
  const months = Array.from(new Set(table.rows.map((row) => monthKey(text(row[index]))))).sort();
  const now = String(basis.getFullYear() * 100 + basis.getMonth() + 1);
  const current = months.indexOf(now) >= 0;
  const ref = current ? now : months[months.length - 1] ?? '';
  const closed = months.filter((month) => month < ref);
  return closed.length ? { index, ref, closed, current } : undefined;
}

/**
 * 표에 든 달(달 칸, 없으면 날짜 칸) — 문서가 말해도 되는 달이다. 10월 판에 1턴 갈래의 「8~10월 조회분」 숫자가 실렸다
 * (2026-10-25 08:54). 날짜가 없으면 빈 목록(가리지 않는다).
 */
export function tableMonths(table: TableData): number[] {
  const monthIndex = monthColumn(table);
  const dateIndex = dateColumn(table);
  const months = table.rows.map((row) => (monthIndex >= 0 ? monthKey(text(row[monthIndex])).slice(4, 6)
    : dateIndex >= 0 && calendarDay(text(row[dateIndex])) ? text(row[dateIndex]).replace(/-/g, '').slice(4, 6) : ''))
    .filter(Boolean).map(Number);
  return Array.from(new Set(months)).sort((left, right) => left - right);
}

/**
 * 보고서가 셀 모집단: 조회한 행 전부를 보고서 규칙이 고른 칸(현장·호기·날짜·담당·상태)으로 담는다. 달마다 조회했으면
 * 「대상월」 칸(조회 조건의 연월)을 앞에 붙인다. 1턴 계산 표 모양은 판마다 달랐다: 4,674행 전부(09:14), 25일까지로 자른
 * 4,334행(09:03), 비고가 있거나 미실시인 1,828행(10:11, 검증 거절), 현장·달별 집계 2,300행(10:23), 예정일이 지난 미실시
 * 110행(10:34). 보고서가 1,828행과 「합계 450」을 〈표 1〉로 싣거나 〈표 1〉 없이 나갔다(2026-10-25).
 * 달은 계산 표에 든 달만 담는다: 8·9·10월을 조회했는데 계산은 과제가 말한 9·10월만 셌다(10:34). 질문과 과제가 말한 달
 * (asked, YYYYMM)은 계산 표에 없어도 담는다: 「지난달 것까지」에 계산 표가 10월만 담은 집계라 9월 조회를 버렸다(2026-10-26 10:00).
 * 계산 표를 그대로 두는 때(undefined): 원자료 칸만 골랐고(계산이 만든 달 칸은 괜찮다) 행이 다 있으며, 달마다 조회했으면
 * 달 칸도 있을 때. 그리고 질문이 말한 값으로 원자료 칸 하나를 좁혔을 때(「B 담당만」, 일부러 만든 표다). 상태 칸을
 * 한 값으로 좁힌 것은 일부러로 보지 않는다(보고서의 〈표 1〉이 상태 구성이고, 미실시는 〈표 2〉가 담는다).
 * 상태 칸이나 날짜 칸을 원자료에서 못 찾으면 만들지 않는다.
 */
export function populationTable(
  table: TableData | undefined,
  retrievals: { label: string; route: string; rowsStateKey: string; conditions: Record<string, string> }[],
  rows: Record<string, unknown>,
  declared: Record<string, { id: string; text?: string }[]>,
  question = '',
  asked: string[] = [],
  /** 자료 읽기. 열린 문제면 읽기의 칸(상태, 발견일, 긴급도, 묶음, 메모)을 담고, 계산 표를 그대로 두지 않는다. */
  reading?: DataReading,
): TableData | undefined {
  if (!table?.columns.length) return undefined;
  // 칸 이름의 「(state)」 꼴은 떼고 견준다. 원자료 칸 설명 끝의 「, 값: …」과 위 표 칸 표시도 뗀다.
  const plain = capTable(table);
  // 코드 풀이 괄호(「결과(%=전체, 1=계획, 2=완료)」)도 뗀다. 「결과(%=전체」로 남았다(10:42).
  const nameOf = (column: { id: string; text?: string }) => (column.text ?? column.id).split(/,| — /)[0]
    ?.replace(/\([^)]*=.*$/, '').trim() || column.id;
  const listOf = (label: string) => (Array.isArray(rows[label]) ? rows[label] as Record<string, unknown>[] : []);
  // 계산 표에 든 달: 달 칸(계산이 만든 것 포함), 없으면 날짜 칸의 연월.
  const madeMonth = monthColumn(plain);
  const dateAt = dateColumn(plain);
  const wanted = new Set(plain.rows.map((one) => (madeMonth >= 0 ? monthKey(text(one[madeMonth]))
    : dateAt >= 0 ? text(one[dateAt]).replace(/-/g, '').slice(0, 6) : '')).filter((key) => monthKey(key)));
  const groups = new Map<string, typeof retrievals>();
  retrievals.forEach((one) => {
    const key = `${one.route}\u0001${one.rowsStateKey}`;
    groups.set(key, [...(groups.get(key) ?? []), one]);
  });
  // 묶음에서 값이 조회마다 다른 연월 조건(planmon=202609·202610).
  const monthNameOf = (group: typeof retrievals) => Object.keys(group[0]?.conditions ?? {})
    .find((name) => group.every((one) => monthKey(one.conditions[name] ?? '')) && new Set(group.map((one) => one.conditions[name])).size > 1);
  let found: TableData | undefined;
  Array.from(groups.values()).some((whole) => {
    const wholeMonth = monthNameOf(whole);
    const group = wholeMonth && wanted.size ? whole.filter((one) => {
      const key = monthKey(one.conditions[wholeMonth] ?? '');
      return wanted.has(key) || asked.indexOf(key) >= 0;
    }) : whole;
    const monthKeyName = monthNameOf(group);
    const labels = group.map((one) => one.label).filter((label) => listOf(label).length && declared[label]?.length);
    const columns = declared[labels[0] ?? ''] ?? [];
    if (!columns.length) return false;
    const names = columns.map(nameOf);
    const raw = labels.flatMap((label) => listOf(label).map((item) => ({ label, item })));
    const rawTable: TableData = { columns: names, rows: raw.map(({ item }) => columns.map((column) => item[column.id])) };
    const monthOf = (label: string) => (monthKeyName
      ? monthKey(group.find((one) => one.label === label)?.conditions[monthKeyName] ?? '') : '');
    // 열린 문제로 읽은 표: 읽기의 칸을 담는다. 짐작한 상태·날짜 칸(처리여부, 계획일자)으로 고르면 결과(B/C), 점검항목장치,
    // 부적합내용이 빠졌다(실제 테넌트, 2026-10-27).
    const idAt = (id: string | undefined) => (id ? columns.findIndex((column) => column.id === id) : -1);
    // 읽은 화면의 묶음에만 쓴다. 칸 id(`state`)는 여러 화면에 있다(독립 검토: 점검결과 행을 결함으로 셀 뻔했다).
    const ownScreen = !reading?.route || (group[0]?.route === reading.route && group[0]?.rowsStateKey === reading.rowsStateKey);
    const issues = reading?.story === 'issues' && ownScreen
      && (idAt(reading.status?.column) >= 0 || idAt(reading.severity?.column) >= 0) ? reading : undefined;
    if (issues) {
      const owner = names.findIndex((name) => OWNER.test(name));
      const marked = [idAt(issues.status?.column), idAt(issues.date?.column), idAt(issues.severity?.column)];
      const site = nameColumn(rawTable, [...marked, owner]);
      const picked = [site, idAt(issues.unit), idAt(issues.date?.column), owner, idAt(issues.status?.column),
        idAt(issues.severity?.column), ...(issues.groups ?? []).map(idAt), ...(issues.notes ?? []).map(idAt)]
        .filter((at, index, all) => at >= 0 && all.indexOf(at) === index);
      // 화면 이름이 없는 메모 칸은 「비고」. 둘이면 둘째부터는 칸 id 그대로(같은 이름이면 읽기가 첫째로만 풀린다, 독립 검토).
      const plainRemarks = picked.filter((at) => /remark/i.test(columns[at]?.id ?? '') && /^[\x00-\x7F]*$/.test(names[at] ?? ''));
      const nameAt = (at: number) => (plainRemarks[0] === at ? '비고' : names[at] ?? '');
      // 질문이 말한 값으로 계산 표를 좁혔으면(「긴급수리만」) 모집단도 그 값의 행만 담는다. 일부러 만든 표다.
      const idOfName = new Map(columns.map((column) => [nameOf(column), column.id] as const));
      const narrowing = plain.columns.flatMap((name, at) => {
        const id = idOfName.get(name);
        const mine = Array.from(new Set(plain.rows.map((one) => text(one[at])).filter(Boolean)));
        const theirs = new Set(raw.map(({ item }) => text(item[id ?? ''])).filter(Boolean));
        return id && mine.length === 1 && theirs.size > 1 && question.includes(mine[0] ?? '\u0000') ? [{ id, value: mine[0] ?? '' }] : [];
      });
      const kept = raw.filter(({ item }) => narrowing.every((one) => text(item[one.id]) === one.value));
      found = capTable({
        columns: [...(monthKeyName ? ['대상월'] : []), ...picked.map(nameAt)],
        rows: kept.map(({ label, item }) => [...(monthKeyName ? [monthOf(label)] : []), ...picked.map((at) => item[columns[at]?.id ?? ''] ?? '')]),
      });
      return true;
    }
    // 보고서 규칙으로 칸을 고른다. 원자료에는 처리자명·보조담당처럼 비슷한 칸이 여럿이다.
    const status = statusColumn(rawTable);
    const date = dateColumn(rawTable);
    if (!status || date < 0) return false;
    const owner = names.findIndex((name) => OWNER.test(name));
    const site = nameColumn(rawTable, [status.index, date, owner]);
    const unit = names.findIndex((name, at) => at !== site && UNIT.test(name));
    const picked = [site, unit, date, owner, status.index].filter((at, index, all) => at >= 0 && all.indexOf(at) === index);
    // 메모(비고) 칸도 담는다. 완료 점검 메모의 긴급수리·고장 기재를 보려면 있어야 한다(12차 검토). 화면 이름이 없는
    // 칸(remark)은 「비고」로 부른다.
    const remark = columns.findIndex((column, at) => picked.indexOf(at) < 0 && /비고|메모|remark|특이/i.test(`${column.id} ${names[at] ?? ''}`));
    // 끝낸 뒤에 채우는 칸(전송, 고객싸인)도 끝에 담는다. 미전송과 서명은 완료 건 중에서 센다(16차 검토). 전송 칸은 값(미전송)으로
    // 찾는다: 화면 이름이 없는 칸(sendflag)이고, 「전송」이라는 화면 이름은 다른 두 칸의 것이었다(8차 검토).
    const kept = remark >= 0 ? [...picked, remark] : picked;
    const sendAt = columns.findIndex((column, at) => kept.indexOf(at) < 0 && raw.some(({ item }) => text(item[column.id]) === '미전송'));
    const signAt = columns.findIndex((_, at) => kept.indexOf(at) < 0 && at !== sendAt && /고객\s*(?:싸인|서명)/.test(names[at] ?? ''));
    const canon = [...kept, ...[sendAt, signAt].filter((at) => at >= 0)];
    const nameAt = (at: number) => (at === sendAt ? '전송'
      : at === remark && /^[\x00-\x7F]*$/.test(names[at] ?? '') ? '비고' : names[at] ?? '');
    const ids = new Map(columns.map((column) => [nameOf(column), column.id] as const));
    const mapped = plain.columns.map((name, at) => (at === madeMonth ? '' : ids.get(name)));
    const projection = mapped.every((id) => id !== undefined);
    // 보고서가 셀 칸(비고, 전송, 고객싸인)이 계산 표에 다 있어야 그대로 둔다. 원자료 칸 여섯에 4,674행을 다 담은 표를 그대로
    // 두었더니 메모, 미전송, 서명 줄을 셀 칸이 없었다(실측 2026-10-26 05:35).
    const covered = canon.every((at) => mapped.indexOf(columns[at]?.id) >= 0);
    if (projection && covered && plain.rows.length >= raw.length && (!monthKeyName || madeMonth >= 0)) return true;
    const statusId = columns[status.index]?.id;
    const narrowed = mapped.some((id, at) => {
      if (!id || id === statusId) return false;
      const mine = Array.from(new Set(plain.rows.map((one) => text(one[at])).filter(Boolean)));
      const theirs = new Set(raw.map(({ item }) => text(item[id])).filter(Boolean));
      return mine.length === 1 && theirs.size > 1 && question.includes(mine[0] ?? '\u0000');
    });
    if (narrowed) return true;
    found = capTable({
      columns: [...(monthKeyName ? ['대상월'] : []), ...canon.map(nameAt)],
      rows: raw.map(({ label, item }) => [...(monthKeyName ? [monthOf(label)] : []), ...canon.map((at) => item[columns[at]?.id ?? ''] ?? '')]),
    });
    return true;
  });
  return found;
}

/** 기준월 행만 — 경과 구분과 그림은 진행 중인 달로 본다. 두 달을 합쳐 그리면 같은 호기를 두 번 센 막대가 된다(10차 검토). */
export function refTable(table: TableData, basis: Date): TableData {
  const scope = monthScope(table, basis);
  return scope ? { ...table, rows: table.rows.filter((row) => monthKey(text(row[scope.index])) === scope.ref) } : table;
}

/** 달 이름 — 「9·10월」. 해가 바뀌면 「'25.12월·'26.1월」. */
function monthsText(months: string[]): string {
  return new Set(months.map((month) => month.slice(0, 4))).size > 1
    ? months.map((month) => shortDate(month)).join('·')
    : `${months.map((month) => Number(month.slice(4))).join('·')}월`;
}

/** 날짜 범위 — 「'26.10.26.~'26.10.31.」, 하루면 그 날. 달력에 없는 날(「'26.11.31.」)은 넣지 않는다. */
function dayRange(rows: unknown[][], dateIndex: number): string {
  const days = rows.map((row) => text(row[dateIndex]).replace(/-/g, '')).filter((day) => calendarDay(day)).sort();
  const first = shortDate(days[0] ?? '');
  const last = shortDate(days[days.length - 1] ?? '');
  return first === last ? first : `${first}~${last}`;
}

export type CarryGroup = {
  key: 'both' | 'prevUpcoming' | 'prevOnly' | 'refOlder' | 'refRecent' | 'olderOnly';
  /** 표 줄 이름 — 「9·10월 모두 미실시」. */
  label: string;
  /** 근거 이름 — 「9·10월 모두 미실시(10월 예정일도 경과)」. */
  detail: string;
  /** 호기(현장명·호기명). */
  units: string[];
  /** 이 묶음의 미실시 행 — 마감된 달 행과 기준월 경과 행. 건수다. */
  rows: unknown[][];
  /** 이 묶음 호기의 기준월 행 — 표의 「10월 예정일」 칸. */
  refRows: unknown[][];
};

export type CarryOver = {
  refName: string; closedName: string; monthsName: string; closedCount: number; attention: string;
  /** 기준월이 오늘이 속한 달인가 — 아니면 모든 달이 마감이라 「N일 현재」·비교 불가를 쓰지 않는다. */
  current: boolean;
  /**
   * 기준월 행이 오늘까지 예정분만인가 — 1턴 계산이 10월을 25일까지 예정분(2,009건)으로 잘라 「점검 대상 2,009건」이
   * 10월 전체처럼 읽혔다(2026-10-25 09:03). 마감된 달은 오늘 날짜보다 늦은 날까지 계획이 있었을 때만 그렇게 본다.
   */
  until: boolean;
  groups: CarryGroup[];
  /** 호기 수와 건수 — 73호기(111건) = 마감된 달 51건 + 기준월 경과 60건. */
  units: number; rows: number; closedRows: number; refRows: number;
  /** 담당별 마감된 달 [배정, 완료] — 「9월은 49건 중 14건 완료」(10차 검토: 「두 달 0건」은 틀린 말이다). */
  history: Map<string, [number, number]>;
  unitOf: (row: unknown[]) => string;
  site: number; unit: number; owner: number; dateIndex: number; siteLabel: string; unitLabel: string;
};

/**
 * **두 달 이상을 조회한 표의 미실시를 호기 단위로 묶는다.** 같은 호기가 달마다 한 번씩 세어져 「11~54일 경과 103건」이 실은
 * 65호기였고, 가장 무거운 「두 달 연속 미실시」가 가려졌다(국가직 팀장 10차 검토, 2026-10-25). 묶음은 무거운 차례로 —
 * 두 달 모두 미실시 · 전월 미실시·이번 달 예정 · 전월만 미실시 · 이번 달만 경과 · 최근 예정분.
 * 호기 칸이 없으면 마감된 달 미실시는 한 묶음(「9월분 미실시」)이다 — 같은 호기인지 모른다.
 */
export function carryOver(table: TableData, basis: Date): CarryOver | undefined {
  const scope = monthScope(table, basis);
  const status = statusColumn(table);
  const dateIndex = dateColumn(table);
  if (!scope || !status || dateIndex < 0) return undefined;
  const owner = table.columns.findIndex((column) => OWNER.test(column));
  const site = nameColumn(table, [status.index, dateIndex, owner, scope.index]);
  const unit = table.columns.findIndex((column, at) => at !== site && UNIT.test(column));
  const month = (row: unknown[]) => monthKey(text(row[scope.index]));
  const undone = (row: unknown[]) => plainStatus(text(row[status.index]), status.values) === status.attention;
  // 호기는 현장명·호기명으로 — 현장 칸이 없으면 호기명만으로(숫자뿐인 호기도 달을 넘어 맞춘다, 코드 리뷰).
  const keys = new Map<unknown[], string>();
  table.rows.forEach((row, at) => keys.set(row, unit >= 0
    ? [site, unit].filter((index) => index >= 0).map((index) => text(row[index])).join('\u0001') : `#${at}`));
  const unitOf = (row: unknown[]) => keys.get(row) ?? '';
  const today = dayNumber(basis);
  const workday = dayNumber(previousWorkday(basis));
  // 예정일이 비었거나 달력에 없으면 「unknown」 — 경과로도 세지 않는다(dueSplit 은 날짜 오류로 센다).
  const kind = (row: unknown[]): 'older' | 'recent' | 'later' | 'unknown' | 'done' => {
    if (!undone(row)) return 'done';
    const day = calendarDay(text(row[dateIndex]));
    return !day ? 'unknown' : day >= today ? 'later' : day >= workday ? 'recent' : 'older';
  };
  const byUnit = (rows: unknown[][]) => rows.reduce((all, row) => all.set(unitOf(row), [...(all.get(unitOf(row)) ?? []), row]),
    new Map<string, unknown[][]>());
  // 묶음은 바로 앞 달(prev)로 가른다 — 마감된 달을 한데 묶으니 세 달 판에서 「8·9월 미실시·10월 예정」이 8월과 9월을
  // 둘 다 안 한 것처럼 읽혔다(2026-10-25 08:29). 그 전 달(older)의 미실시는 호기가 든 묶음에 얹고 따로 센다.
  const prev = scope.closed[scope.closed.length - 1] ?? '';
  const older = scope.closed.slice(0, -1);
  const closedRows = table.rows.filter((row) => scope.closed.indexOf(month(row)) >= 0 && undone(row));
  const prevUnits = byUnit(closedRows.filter((row) => month(row) === prev));
  const olderUnits = byUnit(closedRows.filter((row) => month(row) !== prev));
  const refUnits = byUnit(table.rows.filter((row) => month(row) === scope.ref));
  // 기준월 행이 오늘까지 예정분만이면(1턴 계산이 잘랐으면) 기준월 행이 없는 호기는 「계획 없음」이 아니라 오늘 뒤 예정이다 —
  // 「9월 미실시·10월 계획 없음 12호기」로 썼다(2026-10-25 09:03).
  const days = (rows: unknown[][]) => rows.map((row) => calendarDay(text(row[dateIndex]))).filter(Boolean);
  const refDays = days(table.rows.filter((row) => month(row) === scope.ref));
  const closedDays = days(table.rows.filter((row) => scope.closed.indexOf(month(row)) >= 0));
  const monthEnd = new Date(basis.getFullYear(), basis.getMonth() + 1, 0).getDate();
  const until = scope.current && refDays.length > 0 && Math.max(...refDays) <= today && basis.getDate() < monthEnd
    && closedDays.some((day) => day % 100 > basis.getDate());
  const found = new Map<CarryGroup['key'], { units: string[]; rows: unknown[][]; refRows: unknown[][]; earlier: number; beyond: number }>();
  const add = (key: CarryGroup['key'], unitKey: string, rows: unknown[][], refRows: unknown[][], beyond = false) => {
    const earlier = olderUnits.get(unitKey) ?? [];
    const group = found.get(key) ?? { units: [], rows: [], refRows: [], earlier: 0, beyond: 0 };
    group.units.push(unitKey);
    group.rows.push(...earlier, ...rows);
    group.refRows.push(...refRows);
    if (earlier.length && key !== 'olderOnly') group.earlier += 1;
    if (beyond) group.beyond += 1;
    found.set(key, group);
  };
  const seen = new Set<string>();
  let noPlan = 0;
  prevUnits.forEach((rows, unitKey) => {
    seen.add(unitKey);
    const mine = refUnits.get(unitKey) ?? [];
    const late = mine.filter((row) => kind(row) === 'older' || kind(row) === 'recent');
    const later = mine.filter((row) => kind(row) === 'later' || kind(row) === 'unknown');
    if (late.length) add('both', unitKey, [...rows, ...late], late);
    else if (later.length) add('prevUpcoming', unitKey, rows, later);
    else if (!mine.length && until) add('prevUpcoming', unitKey, rows, [], true);
    else {
      if (!mine.length) noPlan += 1;
      add('prevOnly', unitKey, rows, []);
    }
  });
  refUnits.forEach((mine, unitKey) => {
    if (seen.has(unitKey)) return;
    const late = mine.filter((row) => kind(row) === 'older');
    const recent = mine.filter((row) => kind(row) === 'recent');
    if (late.length || recent.length) seen.add(unitKey);
    if (late.length) add('refOlder', unitKey, [...late, ...recent], [...late, ...recent]);
    else if (recent.length) add('refRecent', unitKey, recent, recent);
  });
  olderUnits.forEach((_, unitKey) => {
    if (!seen.has(unitKey)) add('olderOnly', unitKey, [], []);
  });
  const closedName = monthsText(scope.closed);
  const prevName = monthsText([prev]);
  const olderName = monthsText(older);
  const refName = monthsText([scope.ref]);
  const monthsName = monthsText([...scope.closed, scope.ref]);
  const word = status.attention;
  const unitWord = unit >= 0 ? (table.columns[unit] ?? '').replace(/명$/, '') : '건';
  const both = `${monthsText([prev, scope.ref])} 모두 ${word}`;
  const asDate = (value: number) => new Date(Math.floor(value / 10000), Math.floor(value / 100) % 100 - 1, value % 100);
  const since = (rows: unknown[][]) => {
    const days = rows.map((row) => calendarDay(text(row[dateIndex]))).filter(Boolean)
      .map((day) => Math.round((asDate(today).getTime() - asDate(day).getTime()) / 86400000));
    if (!days.length) return '';
    const [least, most] = [Math.min(...days), Math.max(...days)];
    return least === most ? `${least}일` : `${least}~${most}일`;
  };
  const prevOnly = found.get('prevOnly');
  const plans = !prevOnly ? '' : !noPlan ? '완료' : noPlan === prevOnly.units.length ? '계획 없음' : '완료·계획 없음';
  // [표 줄 이름, 근거 이름, 괄호 말] — 괄호에는 그 전 달 미실시 호기 수를 덧붙인다.
  const named: Record<CarryGroup['key'], (group: { refRows: unknown[][] }) => [string, string, string]> = {
    both: () => [both, both, `${refName} 예정일도 경과`],
    prevUpcoming: (group) => [`${prevName} ${word}·${refName} 예정`, `${prevName} ${word}, ${refName}은 예정`, dayRange(group.refRows, dateIndex)],
    prevOnly: () => (unit >= 0
      ? [`${prevName} ${word}·${refName} ${plans}`, `${prevName} ${word}·${refName} ${plans}`, '']
      : [`${prevName}분 ${word}`, `${prevName}분 ${word}`, `마감 뒤에도 ${word}`]),
    // 경과 일수만 쓰면 날짜로 읽힌다: 1턴 답이 「(19~24일)」을 「10월 19~24일 예정분」으로 썼다(13차 검토).
    refOlder: (group) => [`${refName}만 예정일 경과`, `${refName}만 예정일 경과`,
      `${dayRange(group.refRows, dateIndex)} 예정, ${since(group.refRows)} 경과`],
    refRecent: (group) => [`${dayRange(group.refRows, dateIndex)} 예정분`, `${dayRange(group.refRows, dateIndex)} 예정분`, ''],
    olderOnly: () => [`${olderName}분 ${word}`, `${olderName}분 ${word}`, unit >= 0 ? `그 뒤 경과 ${word} 없음` : ''],
  };
  const order: CarryGroup['key'][] = ['both', 'prevUpcoming', 'prevOnly', 'refOlder', 'refRecent', 'olderOnly'];
  const groups = order.flatMap((key) => {
    const entry = found.get(key);
    if (!entry) return [];
    const { earlier, beyond, ...group } = entry;
    const [label, base, note] = named[key](group);
    const notes = [note, beyond ? `${basis.getDate()}일 뒤라 자료에 없음` : '',
      earlier ? `그중 ${olderName}에도 ${word} ${earlier}${unitWord}` : ''].filter(Boolean);
    return [{ key, label, detail: notes.length ? `${base}(${notes.join(', ')})` : base, ...group }];
  });
  const history = new Map<string, [number, number]>();
  if (owner >= 0) {
    table.rows.filter((row) => scope.closed.indexOf(month(row)) >= 0).forEach((row) => {
      const [assigned, done] = history.get(text(row[owner])) ?? [0, 0];
      history.set(text(row[owner]), [assigned + 1, done + (undone(row) ? 0 : 1)]);
    });
  }
  return {
    refName, closedName, monthsName, closedCount: scope.closed.length, attention: word, current: scope.current, until, groups,
    units: groups.reduce((sum, group) => sum + group.units.length, 0),
    rows: groups.reduce((sum, group) => sum + group.rows.length, 0),
    closedRows: closedRows.length,
    refRows: groups.reduce((sum, group) => sum + group.rows.length, 0) - closedRows.length,
    history, unitOf, site, unit, owner, dateIndex,
    siteLabel: site >= 0 ? (table.columns[site] ?? '').replace(/명$/, '') : '',
    unitLabel: unit >= 0 ? (table.columns[unit] ?? '').replace(/명$/, '') : '',
  };
}

/** 묶음 호기의 담당별 호기 수 — 기준월 행의 담당(지금 맡은 사람), 없으면 마감된 달 행의 담당. */
function unitOwners(carry: CarryOver, group: CarryGroup, units: string[] = group.units): [string, number][] {
  if (carry.owner < 0) return [];
  const counts = new Map<string, number>();
  units.forEach((unitKey) => {
    const row = group.refRows.find((one) => carry.unitOf(one) === unitKey) ?? group.rows.find((one) => carry.unitOf(one) === unitKey);
    const name = row ? text(row[carry.owner]) : '';
    if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
  });
  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
}

/** 묶음 호기의 현장별 호기 수. */
function unitSites(carry: CarryOver, group: CarryGroup): [string, string[]][] {
  const sites = new Map<string, string[]>();
  group.units.forEach((unitKey) => {
    const row = group.rows.find((one) => carry.unitOf(one) === unitKey);
    const name = row && carry.site >= 0 ? text(row[carry.site]) : '';
    sites.set(name, [...(sites.get(name) ?? []), unitKey]);
  });
  return Array.from(sites.entries()).sort((left, right) => right[1].length - left[1].length);
}

/** 가장 큰 이름 묶음(괄호 앞이 같은 현장, 예: 「가나본부」 3개 현장 35호기). 절반을 넘을 때만. */
function unitFamily(sites: [string, string[]][], total: number): { family: string; sites: [string, string[]][]; units: number } | undefined {
  const families = new Map<string, [string, string[]][]>();
  sites.forEach((entry) => {
    const family = entry[0].replace(/\s*\(.*\)\s*$/, '').trim();
    families.set(family, [...(families.get(family) ?? []), entry]);
  });
  const best = Array.from(families.entries())
    .map(([family, members]) => ({ family, sites: members, units: members.reduce((sum, [, units]) => sum + units.length, 0) }))
    .sort((left, right) => right.units - left.units)[0];
  return best && best.family && best.sites.length > 1 && best.units * 2 >= total ? best : undefined;
}

/**
 * 〈표 1〉 달 줄의 시점 — 「'26.9월(마감)」·「'26.10월(25일 현재)」. 마감된 달과 진행 중인 달의 비율(2.2%·16.9%)을 나란히
 * 놓아 1턴에서 막은 비교를 문서가 대신했다(10차 검토).
 */
export function monthStates(table: TableData, basis: Date, carry?: CarryOver): TableData {
  const MONTH_ROW = /^'(\d{2})\.(\d{1,2})월$/;
  // 기준월이 오늘까지 예정분만이면 「현재」가 아니라 「N일까지 예정분」 — 10월 전체가 아니다(2026-10-25 09:03).
  const now = carry?.until ? `${basis.getDate()}일까지 예정분` : `${basis.getDate()}일 현재`;
  const current = (basis.getFullYear() % 100) * 100 + basis.getMonth() + 1;
  const value = (label: string) => {
    const found = label.match(MONTH_ROW);
    return found ? Number(found[1]) * 100 + Number(found[2]) : 0;
  };
  if (table.rows.filter((row) => value(String(row[0] ?? ''))).length < 2) return table;
  return {
    ...table,
    rows: table.rows.map((row) => {
      const label = String(row[0] ?? '');
      const at = value(label);
      if (!at) return row;
      return [at === current ? `${label}(${now})` : at < current ? `${label}(마감)` : label, ...row.slice(1)];
    }),
  };
}

/**
 * 두 달 모두 미실시 줄의 건수 괄호를 「(두 달 76건)」으로 쓴다. 「38호기 우선 조치 필요(10월 예정일 경과 포함, 76건)」은
 * 무엇을 포함한다는 말인지 분명하지 않았다(13차 검토). 그 묶음의 호기 수가 든 줄에서, 그 묶음 건수로 끝나는 괄호만 고친다.
 * 세 달 판은 그 묶음 건수에 그 전 달 미실시가 얹혀 두 달 건수가 아니라 그대로 둔다.
 */
export function withBothSpan(lines: string[], carry: CarryOver | undefined): string[] {
  const both = carry?.closedCount === 1 ? carry.groups.find((group) => group.key === 'both') : undefined;
  if (!both) return lines;
  const units = new RegExp(`(?<![\\d,])${both.units.length}${carry?.unitLabel || '호기'}`);
  const rows = both.rows.length.toLocaleString('en-US');
  const span = new RegExp(`\\(([^()]*?)(?<![\\d,])${rows}건\\)`);
  return lines.map((line) => (units.test(line) ? line.replace(span, `(두 달 ${rows}건)`) : line));
}

/**
 * 여러 달 판에서 요약·확인 줄의 숫자가 〈표 1〉 한 달 줄과만 맞으면 그 달 이름을 앞에 붙인다. 「점검 대상 2,348건 중
 * 완료 1,952건(83.1%), 25일 현재」가 어느 달 것인지 안 보였다(11차 검토). 예시를 고쳐도 모델 문장은 판마다 달라 코드가
 * 붙인다. 달을 이미 말했거나, 두 숫자 넘게 맞는 달 줄이 하나가 아니면 그대로 둔다(숫자 하나는 우연히 맞는다).
 */
export function withMonthLabels(lines: string[], status: TableData): string[] {
  const rows = status.rows.filter((row) => /^'\d{2}\.\d{1,2}월/.test(String(row[0] ?? '')));
  if (rows.length < 2) return lines;
  // 해가 같으면 「'26.」을 뗀다(「10월(25일 현재)」).
  const sameYear = new Set(rows.map((row) => String(row[0]).slice(1, 3))).size === 1;
  const numbers = (line: string) => (line.replace(/(\d),(\d{3})/g, '$1$2').match(/\d+(?:\.\d+)?/g) ?? []).map(Number);
  return lines.map((line) => {
    const mine = numbers(line);
    // 끝 칸은 비율이라 빼고 건수만 견준다.
    const hits = rows.filter((row) => row.slice(1, -1).filter((value) => mine.indexOf(Number(value)) >= 0).length >= 2);
    if (hits.length !== 1) return line;
    const label = String(hits[0]?.[0] ?? '');
    if (!/(?<![\d.'])\d{1,2}\s*월/.test(line)) {
      return `${sameYear ? label.replace(/^'\d{2}\./, '') : label} ${line.replace(/,?\s*\d{1,2}일\s*현재\s*$/, '').trim()}`;
    }
    // 달은 말했는데 시점이 없으면 그 달 이름 뒤에 붙인다: 「10월 점검 대상 2,348건 …」(실측 13:06). 시점 말이 이미 있으면 둔다.
    const [, name, state] = label.match(/^(?:'\d{2}\.)?(\d{1,2}월)(\(.+\))$/) ?? [];
    if (!name || !state || line.indexOf(state.slice(1, -1)) >= 0) return line;
    return line.replace(new RegExp(`(?<![\\d.'·])${name}(?!\\s*\\()`), `${name}${state}`);
  });
}

/**
 * 두 달 판의 〈표 1〉 아래 줄 — 마감 수치와 진행 중 수치는 비율을 견줄 수 없다(10차 검토). 모든 달이 마감이면(오늘이
 * 속한 달이 자료에 없으면) 견줄 수 있으니 쓰지 않는다(코드 리뷰).
 */
export function monthNote(carry: CarryOver, due: DueSplit, basis: Date): string | undefined {
  if (!carry.current) return undefined;
  const number = (value: number) => value.toLocaleString('en-US');
  if (carry.until) {
    return `${carry.closedName}은 마감 수치, ${carry.refName}은 ${basis.getDate()}일까지 예정분이라 ${due.attention} 비율 직접 비교 불가`;
  }
  return `${carry.closedName}은 마감 수치, ${carry.refName}은 ${basis.getDate()}일 현재 수치라 ${due.attention} 비율 직접 비교 불가`
    + `(${carry.refName} ${due.attention} ${number(due.total)}건 중 예정일 이내 ${number(due.today + due.upcoming)}건)`;
}

/** 원자료의 없는 날짜(20260931) — 경과를 셀 수 없다. ※ 줄로 밝히고 ERP 자료를 고치게 한다(10차 검토). */
export function dateErrorNote(table: TableData): string | undefined {
  const dateIndex = dateColumn(table);
  if (dateIndex < 0) return undefined;
  const bad = table.rows.filter((row) => text(row[dateIndex]) && !calendarDay(text(row[dateIndex])));
  if (!bad.length) return undefined;
  const owner = table.columns.findIndex((column) => OWNER.test(column));
  const site = nameColumn(table, [statusColumn(table)?.index ?? -1, dateIndex, owner]);
  const unit = table.columns.findIndex((column, at) => at !== site && UNIT.test(column));
  const samples = bad.slice(0, 3).map((row) => [shortDate(text(row[dateIndex])),
    [site, unit].filter((at) => at >= 0).map((at) => text(row[at])).join(' ')].filter(Boolean).join(', '));
  // 문서·채팅에 그대로 나가는 줄이라 em-dash 대신 쉼표(11차 검토, 사용자 전역 지침 stop-slop-ko).
  return `원자료 ${table.columns[dateIndex]} 오류 ${bad.length.toLocaleString('en-US')}건(${samples.join(' / ')}), ERP 자료 수정 필요`;
}

/**
 * 배정분을 하나도 못 한 담당의 사유 확인 — 모델이 빠뜨리면 맨 앞에 넣는다. 기한은 다음 근무일. 두 달 판은 어느 달
 * 배정인지 밝힌다(10차 검토: 07:15 판에서 이 조치가 빠졌다).
 */
export function withIdleActions(actions: string[], due: DueSplit, basis: Date, month?: string): string[] {
  // 담당이 앞에 있으면 괄호에 되풀이하지 않는다(2026-10-25 08:29: 「A 담당 … 사유 확인('26.10.26.까지, A)」).
  const tidy = actions.map((action) => action.replace(/^(.*?(\S+) 담당.*), \2\)$/, '$1)'));
  const missing = due.idleOwners.filter(([name]) => !tidy.some((action) => action.includes(name) && /사유/.test(action)));
  return [...missing.map(([name, assigned]) => `${name} 담당 ${month ? `${month} ` : ''}배정 ${assigned.toLocaleString('en-US')}건`
    + ` 미실시 사유 확인(${dayLabel(nextWorkday(basis))}까지)`), ...tidy];
}

/**
 * ※ 날짜 오류 줄(dataNotes)을 되풀이한 확인 사항은 뺀다. 「점검일자 '26.9.31. 기록 1건의 날짜 정정 필요」가 ※ 줄과
 * 함께 실렸다(2026-10-25 10:51). 그 줄의 날짜('26.9.31.)를 말하면 같은 일이다.
 */
export function notRepeating(checks: string[], note: string | undefined): string[] {
  const days = note?.match(/'\d{2}\.\d{1,2}\.\d{1,2}\./g) ?? [];
  // 둥근 따옴표(‘26.9.31.’)도 같은 날짜다(실측 2026-10-26 08:49: 모델이 그렇게 써 되풀이를 못 걸렀다).
  const plain = (text: string) => text.replace(/[‘’`]/g, "'");
  return days.length ? checks.filter((check) => !days.some((day) => plain(check).includes(day))) : checks;
}

/** 모델이 쓴 미실시 조치 — 여러 달 판에서는 코드가 쓴 묶음별 조치가 대신한다. */
const OVERDUE_ACTION = /미실시|예정일|경과|예정분|예정\s*\d|사유|점검\s*완료|실시·입력|입력\s*여부/;

/**
 * 여러 달 판의 향후 조치 — 묶음마다 하나, 두 달 모두 미실시를 먼저(10차 검토). 모델이 두 번(08:29, 09:14) 묶음을 섞어
 * 「예정일보다 11~24일 지난 52건 점검 완료」로 쓰고 9월 미실시·10월 예정 13호기를 빠뜨려 코드가 쓴다.
 * 모델이 쓴 미실시 조치는 빼고, 다른 일(전송·서명)의 조치는 뒤에 둔다.
 *
 * 사유 확인은 두 달 모두 미실시 호기 전부와, 기준월 배정분을 하나도 못 한 담당의 나머지 기준월 경과 호기를 한 줄로 쓴다
 * (11차 검토). 담당 완료 실적으로만 고르면 다른 3건을 끝낸 C의 두 달 모두 미실시 3호기가 빠지고, A는 9월에
 * 끝낸 중앙역·남역 14호기가 10월에 0건인데도 빠졌다. 그 묶음이 없으면 담당마다 어느 달 배정인지로 쓴다.
 */
export function carryActions(actions: string[], carry: CarryOver, due: DueSplit | undefined, basis: Date): string[] {
  const number = (value: number) => value.toLocaleString('en-US');
  const unitWord = carry.unitLabel || '건';
  const next = dayLabel(nextWorkday(basis));
  const monthEnd = dayLabel(new Date(basis.getFullYear(), basis.getMonth() + 1, 0));
  // 담당은 셋까지 — 괄호 속 괄호(「외 2명(2호기))」)가 되지 않게 남은 수는 뗀다(〈표 2〉에 있다).
  const few = (pairs: [string, number][]) => fewCounts(pairs, unitWord).replace(/\([^)]*\)$/, '');
  const owners = (group: CarryGroup) => few(unitOwners(carry, group));
  const tail = (deadline: string, who: string) => `(${[`${deadline}까지`, who].filter(Boolean).join(', ')})`;
  const both = carry.groups.find((group) => group.key === 'both');
  const idle = due?.idleOwners ?? [];
  const lateGroups = carry.groups.filter((group) => group.key === 'refOlder' || group.key === 'refRecent');
  // 완료 0건인 담당의 기준월 경과 호기 중 두 달 모두 미실시 묶음 밖의 것.
  const extras = both ? idle.map(([name]) => ({
    name, count: lateGroups.reduce((sum, group) => sum + (unitOwners(carry, group).find(([one]) => one === name)?.[1] ?? 0), 0),
  })).filter((one) => one.count > 0) : [];
  const tally = new Map(both ? unitOwners(carry, both) : []);
  extras.forEach(({ name, count }) => tally.set(name, (tally.get(name) ?? 0) + count));
  const head = both ? `${both.label} ${number(both.units.length)}${unitWord}` : '';
  const reasons = both
    ? [`${extras.length
      ? `${withParticle(head, '과', '와')} ${extras.map(({ name, count }) => `${name} 담당 ${carry.refName}분 ${number(count)}${unitWord}`)
        .join(', ')}`
      : head} 사유 확인${tail(next, few(Array.from(tally.entries()).sort((left, right) => right[1] - left[1])))}`]
    : idle.map(([name, assigned]) => `${name} 담당 ${carry.refName} 배정 ${number(assigned)}건 ${carry.attention} 사유 확인(${next}까지)`);
  const lines = carry.groups.map((group) => {
    const count = `${number(group.units.length)}${unitWord}`;
    if (group.key === 'both' || group.key === 'refOlder') return `${group.label} ${count} 점검 완료${tail(monthEnd, owners(group))}`;
    if (group.key === 'refRecent') {
      return `${dayRange(group.refRows, carry.dateIndex)} 예정 ${number(group.rows.length)}건 실시·입력 여부 확인${tail(next, owners(group))}`;
    }
    if (group.key === 'prevUpcoming') {
      // 기준월 예정일이 남았으면 그 날이 기한이다 — 확인 기한(다음 근무일)으로 당기지 않는다.
      const days = dayRange(group.refRows, carry.dateIndex);
      return days ? `${group.label} ${count} 예정일(${days}) 점검 완료${owners(group) ? `(${owners(group)})` : ''}`
        : `${group.label} ${count} 점검 완료${tail(monthEnd, owners(group))}`;
    }
    // 전월만 미실시(기준월 완료·계획 없음)와 그 전 달분 — 지난 달은 되돌려 할 수 없으니 사유와 기준월 계획을 확인한다.
    const noPlan = /계획 없음/.test(group.label);
    const what = noPlan && /완료/.test(group.label) ? `${carry.attention} 사유·${carry.refName} 계획 확인`
      : noPlan ? `${carry.refName} 점검 계획 확인`
        : group.label.endsWith(carry.attention) ? '사유 확인' : `${carry.attention} 사유 확인`;
    return `${group.label} ${count} ${what}${tail(next, owners(group))}`;
  });
  return [...reasons, ...lines, ...actions.filter((action) => !OVERDUE_ACTION.test(action))];
}

/**
 * 여러 달 판은 달별 그림을 싣지 않는다 — 그림은 기준월만(10차 검토). 마감된 달과 진행 중인 달을 나란히 그려
 * 1턴에서 막은 비교를 그림이 대신했다(8·9·10월 막대, 2026-10-25 08:29).
 */
export function currentCharts<T extends { categories: string[] }>(charts: T[], multi: boolean): T[] {
  // 범주가 「'26.9월」이든 「2026년 9월」이든 달이면 달별 그림이다(2026-10-25 09:03).
  return multi
    ? charts.filter((chart) => !(chart.categories.length > 1
      && chart.categories.every((category) => /^'\d{2}\.\d{1,2}월$/.test(category) || monthKey(category))))
    : charts;
}

/** 모델에게 넘길 표·비율 근거. 비율과 경과·예정 구분은 여기서 센다 — 모델이 지어내지 않고 옮기게. */
export function reportFacts(
  status: { table: TableData } | undefined,
  exceptions: { title: string; table: TableData; total: number } | undefined,
  due?: DueSplit,
  basis?: Date,
  /** 두 달 이상을 조회한 판의 호기 묶음. 있으면 경과 근거는 기준월 것이라 달 이름을 붙인다. */
  carry?: CarryOver,
): string[] {
  const facts: string[] = [];
  if (status) {
    const [, , ...names] = status.table.columns;
    const counts = names.slice(0, -1);
    status.table.rows.forEach((row) => {
      const total = Number(row[1]) || 0;
      const parts = counts.map((name, index) => {
        const count = Number(row[index + 2]) || 0;
        const share = total ? Math.round((count / total) * 1000) / 10 : 0;
        return `${name} ${count.toLocaleString('en-US')}건(${share}%)`;
      });
      facts.push(`${row[0]}: 전체 ${total.toLocaleString('en-US')}건, ${parts.join(', ')}`);
    });
  }
  const number = (value: number) => value.toLocaleString('en-US');
  if (carry && basis) facts.push(...carryFacts(carry, basis));
  const lead = carry ? `${carry.refName} ` : '';
  if (due && basis) {
    const tomorrow = new Date(basis.getFullYear(), basis.getMonth(), basis.getDate() + 1);
    facts.push(`${lead}${due.attention} ${number(due.total)}건 중 예정일 경과 ${number(due.overdue)}건, `
      + `당일(${dayLabel(basis)}) 예정 ${number(due.today)}건, 이후 예정 ${number(due.upcoming)}건`
      + `${due.lastUpcoming ? `(${dayLabel(tomorrow)}~${due.lastUpcoming})` : ''}`
      + `${due.invalid ? `, 날짜 오류 ${number(due.invalid)}건` : ''}`);
    // 「남은 예정 336건」이 당일 50건을 빠뜨렸다(4차 검토) — 경과 외 합계를 셈해 준다.
    if (due.today && due.upcoming) {
      facts.push(`${lead}경과 외 ${due.attention} ${number(due.today + due.upcoming)}건(당일 ${number(due.today)}건 + 이후 ${number(due.upcoming)}건)`);
    }
    const list = (pairs: [string, number][]) => pairs.slice(0, 8).map(([name, count]) => `${name} ${number(count)}건`).join(', ');
    if (due.bySite.length) facts.push(`${lead}예정일 경과 ${number(due.overdue)}건 ${due.siteLabel}별: ${list(due.bySite)}`);
    if (due.byOwner.length) facts.push(`${lead}예정일 경과 ${number(due.overdue)}건 담당별: ${list(due.byOwner)}`);
    // 오래 지난 경과와 하루 지난 경과는 조치가 다르다 — 같은 「점검 완료」를 걸었다(7차 검토).
    if (due.recent && due.olderLast && due.recent < due.overdue) {
      const olderOwners = fewCounts(due.olderOwners);
      const recentOwners = fewCounts(due.recentOwners);
      // 최근 예정분은 날짜로만 — 「직전 근무일 이후」는 앞으로 올 예정처럼 읽혔고(8차), 「최근 예정분」 같은 시점어는
      // 공문에서 날짜로 쓴다(9차 검토).
      facts.push(`${lead}예정일 경과 ${number(due.overdue)}건 = ${olderSpan(due)} 경과 ${number(due.overdue - due.recent)}건`
        + `(${olderDates(due)} 예정${olderOwners ? `, ${olderOwners}` : ''})`
        + ` + ${recentDays(due)} 예정 ${number(due.recent)}건${recentOwners ? `(${recentOwners})` : ''}`
        + ', 이 날짜 예정분은 실시 후 미입력일 수 있음');
      facts.push(`다음 근무일: ${dayLabel(nextWorkday(basis))}`);
    }
    // 한 사람이 배정분을 하나도 못 했으면 기한만 정한 조치로는 부족하다(8차 검토). 두 달 판은 어느 달 배정인지와
    // 마감된 달 실적까지 쓴다. A는 9월에 14건을 했다(10차 검토: 「두 달 0건」은 틀린 문장).
    due.idleOwners.forEach(([name, assigned, late]) => {
      const past = carry?.history.get(name);
      facts.push(`${name} 담당 ${lead}배정 ${number(assigned)}건 중 완료 0건(예정일 경과 ${number(late)}건)`
        + (past ? `, ${carry?.closedName}은 ${number(past[0])}건 중 ${number(past[1])}건 완료` : ''));
    });
    facts.push(...overdueFocus(due, carry));
  } else if (exceptions) {
    const examples = exceptions.table.rows.slice(0, 3).map((row) => row.join(' ')).join('; ');
    facts.push(`${exceptions.title} ${exceptions.total.toLocaleString('en-US')}건 (예: ${examples})`);
  }
  if (basis) facts.push(`이번 달 말일: ${dayLabel(new Date(basis.getFullYear(), basis.getMonth() + 1, 0))}`);
  return facts;
}

/**
 * 두 달 판의 근거 — 호기 묶음, 달별 몫, 묶음마다 현장·담당, 두 달 비율을 견주지 않는다는 것(10차 검토).
 */
function carryFacts(carry: CarryOver, basis: Date): string[] {
  const number = (value: number) => value.toLocaleString('en-US');
  const unitWord = carry.unitLabel || '건';
  const twice = carry.closedCount === 1 ? '두 달에 한 번씩' : '달마다 한 번씩';
  // 마감 수치와 진행 중 수치는 견주지 않는다 — 모든 달이 마감이면 이 줄은 없다(코드 리뷰: 「10월은 2일 현재」).
  // 기준월이 오늘까지 예정분만이면 그렇다고 밝힌다 — 「점검 대상 2,009건」이 10월 전체처럼 읽혔다(2026-10-25 09:03).
  // 근거 글에도 em-dash를 쓰지 않는다(사용자 전역 지침 stop-slop-ko): 모델이 근거의 부호를 문서로 옮긴다.
  const compare = carry.current
    ? [`${carry.closedName}은 마감, ${carry.refName}은 ${basis.getDate()}일 현재라 `
      + `${carry.closedCount === 1 ? '두 달' : '달별'} ${carry.attention} 비율을 견주지 않는다`,
    ...(carry.until ? [`${carry.refName}은 ${basis.getDate()}일까지 예정분만 있고 그 뒤 예정분은 자료에 없다`] : [])]
    : [];
  // 묶음이 없으면(미실시 경과가 없으면) 묶음 줄을 쓰지 않는다. 「0호기(0건) =」로 깨졌다(코드 리뷰).
  if (!carry.groups.length) return compare;
  // 두 달을 더한 수는 마감된 달 미실시(모두 예정일이 지났다)와 기준월 예정일 경과의 합이다. 「미실시」라고만 부르면 기준월
  // 미실시(결과 「계획」 전체)와 부딪힌다(실측 2026-10-26 06:26: 1턴이 「두 달의 미실시는 103건」이라 썼다).
  const facts = [
    `${carry.monthsName} 예정일 경과 ${carry.attention} ${number(carry.units)}${unitWord}(${number(carry.rows)}건) = `
      + carry.groups.map((group) => `${group.detail} ${number(group.units.length)}${unitWord}`
        + (group.rows.length !== group.units.length ? `(${number(group.rows.length)}건)` : '')).join(' + '),
    ...(carry.unit >= 0 ? [`같은 ${unitWord}가 ${twice} 세어지므로 ${unitWord}로 센다`] : []),
    `${number(carry.rows)}건 = ${carry.closedName}분 ${number(carry.closedRows)}건 + ${carry.refName}분 ${number(carry.refRows)}건`,
  ];
  // 마감된 달 미실시의 담당 전부. 1턴 답이 「9월 미실시, 10월은 예정」 묶음의 담당(E 5·B 4·D 2)을
  // 9월 미실시 51건의 담당으로 쓰고 「나머지 2건」이라 했다(13차 검토: 나머지는 40건).
  const closedOwners = Array.from(carry.history.entries()).filter(([name]) => !!name)
    .map(([name, [assigned, done]]) => [name, assigned - done] as [string, number]).filter(([, left]) => left > 0)
    .sort((left, right) => right[1] - left[1]);
  if (closedOwners.length) {
    facts.push(`${carry.closedName} ${carry.attention} ${number(carry.closedRows)}건 담당별: `
      + `${closedOwners.slice(0, 8).map(([name, left]) => `${name} ${number(left)}건`).join(', ')}${closedOwners.length > 8 ? ' 등' : ''}`);
  }
  carry.groups.forEach((group, at) => {
    const sites = unitSites(carry, group).slice(0, 8).map(([name, units]) => `${name} ${number(units.length)}`).join(', ');
    const owners = fewCounts(unitOwners(carry, group), unitWord);
    facts.push(`${group.detail} ${number(group.units.length)}${unitWord}: `
      + [at === 0 && carry.site >= 0 ? `${carry.siteLabel}별 ${sites}` : '', owners ? `담당별 ${owners}` : ''].filter(Boolean).join(' / '));
  });
  return [...facts, ...compare];
}

/**
 * 1턴 답에 싣는 묶음 문장(13차 검토). 〈표 2〉와 같은 묶음을 코드가 문장으로 쓴다. 1턴 답이 묶음을 다시 풀어 쓰다
 * 「10월 19~24일 예정분 중 14호기」(경과 일수를 날짜로 읽었다), 「10월 1~14일 예정분 49건은 모두 A 담당」(52건 중
 * A 49건)으로 틀렸다. 끝 줄은 기준월 경과의 날짜별 몫. 묶음이 없으면(한 달 판) 빈 목록이다.
 */
export function carryLines(table: TableData, basis: Date): string[] {
  const carry = carryOver(table, basis);
  if (!carry?.groups.length) return [];
  const number = (value: number) => value.toLocaleString('en-US');
  const unitWord = carry.unitLabel || '건';
  const two = carry.closedCount === 1;
  // 묶음 건수가 어느 달 몫인지. 세 달 판은 그 전 달 미실시가 얹혀 달 이름을 붙이지 않는다.
  const share = (group: CarryGroup) => {
    const rows = `${number(group.rows.length)}건`;
    if (group.key === 'both') return two ? `두 달 ${rows}` : rows;
    if (group.key === 'prevUpcoming' || group.key === 'prevOnly') return two ? `${carry.closedName}분 ${rows}` : rows;
    return group.key === 'refOlder' || group.key === 'refRecent' ? `${carry.refName}분 ${rows}` : rows;
  };
  const lines = carry.groups.map((group) => {
    // 최근 예정분은 묶음 이름이 날짜다.
    const dates = group.key !== 'refRecent' && group.refRows.length
      ? `${carry.refName} 예정일 ${dayRange(group.refRows, carry.dateIndex)}` : '';
    const tail = [dates, fewCounts(unitOwners(carry, group), unitWord)].filter(Boolean).join(', ');
    const head = carry.unit >= 0 ? `${group.label} ${number(group.units.length)}${unitWord}(${share(group)})` : `${group.label} ${share(group)}`;
    return `${head}${tail ? `: ${tail}` : ''}`;
  });
  const due = dueSplit(refTable(table, basis), basis);
  if (due?.recent && due.olderLast && due.recent < due.overdue) {
    const olderOwners = fewCounts(due.olderOwners);
    const recentOwners = fewCounts(due.recentOwners);
    lines.push(`${carry.refName} 예정일 경과 ${number(due.overdue)}건: ${olderDates(due)} 예정 ${number(due.overdue - due.recent)}건`
      + `${olderOwners ? `(${olderOwners})` : ''}, ${recentDays(due)} 예정 ${number(due.recent)}건${recentOwners ? `(${recentOwners})` : ''}`);
  } else if (due?.overdue) {
    // 최근 예정분이 없어도 경과 전체의 예정일·담당을 쓴다. 1턴 답이 「10월만 예정일 경과」 묶음의 범위(10월 1~6일)를 경과 전체의
    // 예정일로 썼다(실측 15:20, 실제 10월 1~14일).
    const owners = fewCounts(due.olderLast ? due.olderOwners : due.recentOwners);
    lines.push(`${carry.refName} 예정일 경과 ${number(due.overdue)}건: ${due.olderLast ? olderDates(due) : recentDays(due)} 예정`
      + `${owners ? `(${owners})` : ''}`);
  }
  return lines;
}

/**
 * 경과 건의 쏠림 — 같은 이름(괄호 앞이 같은 현장)과 한 담당에 몰린 몫. 상사가 볼 것이 이것이다(3차 검토).
 * 「…계열」은 무엇인지 모른다. 「가나본부 5개 현장」처럼 몇 곳인지 쓴다(5차 검토).
 * 근거이자 〈표 2〉 위 줄이다 — 그림 결론이 이것을 되풀이하지 않게 코드가 쓴다.
 * 두 달 판은 「두 달 모두 미실시」 묶음의 호기로 — 가장 무거운 사실이 그것이다(10차 검토).
 */
export function overdueFocus(due: DueSplit | undefined, carry?: CarryOver): string[] {
  const first = carry?.groups[0];
  if (carry && first?.key === 'both') return carryFocus(carry, first, due);
  if (!due?.overdue) return [];
  const number = (value: number) => value.toLocaleString('en-US');
  // 경과를 오래 지난 것과 직전 근무일분으로 나눴으면 오래 지난 것이 분모 — 60과 52가 섞였다(8차 검토).
  const split = !!due.recent && !!due.olderLast && due.recent < due.overdue;
  const total = split ? due.overdue - due.recent : due.overdue;
  const bySite = split ? due.olderBySite : due.bySite;
  const month = carry ? `${carry.refName} ` : '';
  const lead = split ? `${month}${olderSpan(due)} 경과 ${number(total)}건` : `${month}예정일 경과 ${number(total)}건`;
  // 배정분을 하나도 못 한 담당은 그 사실까지 쓴다. 「모두 A 담당」으로는 배정분 전부라는 것이 안 보였다(8차 검토).
  const idle = (name: string) => {
    const found = due.idleOwners.find(([one]) => one === name);
    return found ? `(${month}배정 ${number(found[1])}건 중 완료 0건)` : '';
  };
  return focusOf({
    lead, total, bySite, siteOwners: split ? due.olderSiteOwners : due.siteOwners, byOwner: split ? due.olderOwners : due.byOwner,
    siteLabel: due.siteLabel, idle,
  });
}

/**
 * 경과 건의 같은 이름 현장·담당 쏠림 한 줄(overdueFocus, lateChartLine). strict면 담당 쏠림은 절반을 넘어야 한다(반반은
 * 쏠림이 아니다).
 */
function focusOf(input: {
  lead: string; total: number; bySite: [string, number][]; siteOwners: [string, string[]][]; byOwner: [string, number][];
  siteLabel: string; idle?: (name: string) => string; strict?: boolean;
  /** 그림에 그린 현장. 같은 이름 현장이 그림에 다 없으면 결론 끝에 그림의 범위를 밝힌다(15차 검토). */
  drawn?: string[];
}): string[] {
  const number = (value: number) => value.toLocaleString('en-US');
  const share = (count: number) => Math.round((count / input.total) * 1000) / 10;
  const idle = input.idle ?? (() => '');
  const lines: string[] = [];
  const families = new Map<string, number>();
  input.bySite.forEach(([name, count]) => {
    const family = name.replace(/\s*\(.*\)\s*$/, '').trim();
    families.set(family, (families.get(family) ?? 0) + count);
  });
  const [family, inFamily] = Array.from(families.entries()).sort((left, right) => right[1] - left[1])[0] ?? ['', 0];
  const members = input.bySite.filter(([name]) => name.indexOf(family) === 0).length;
  const [owner, byOwner] = input.byOwner[0] ?? ['', 0];
  if (family && inFamily > 1 && inFamily * 2 >= input.total && members > 1) {
    // 같은 건을 두 줄로 쓰지 않는다. 「가나본부 49건」과 「A 49건」은 같은 49건이었다(6차 검토).
    const familyOwners = Array.from(new Set(input.siteOwners
      .filter(([name]) => name.indexOf(family) === 0)
      .reduce<string[]>((all, [, list]) => all.concat(list), [])));
    const same = familyOwners.length === 1 && familyOwners[0] === owner && byOwner === inFamily;
    // 결론 줄은 5개 현장인데 그림은 상위 4곳이었다(15차 검토).
    const drawn = input.drawn;
    const partial = drawn && input.bySite.some(([name]) => name.indexOf(family) === 0 && drawn.indexOf(name) < 0)
      ? `(그림은 상위 ${drawn.length}곳)` : '';
    lines.push(`${input.lead} 중 ${family} ${members}개 ${input.siteLabel || '곳'} ${number(inFamily)}건(${share(inFamily)}%)`
      + (same ? `, 모두 ${owner} 담당${idle(owner)}` : '') + partial);
    if (same) return lines;
  }
  if (owner && (input.strict ? byOwner * 2 > input.total : byOwner * 2 >= input.total)) {
    lines.push(`${input.lead} 중 ${owner} 담당 ${number(byOwner)}건(${share(byOwner)}%)${idle(owner)}`);
  }
  return lines;
}

/**
 * 경과 순 그림의 결론. 그림을 예정일 경과 순으로 바꾼 뒤 그림 위 결론 줄이 빠졌다(14차 검토). 그림은 기준월 예정일 경과
 * 전부(직전 근무일분 포함)를 세므로 분모도 그렇다. 쏠림이 없으면 쓰지 않는다.
 */
export function lateChartLine(due: DueSplit | undefined, month: string, drawn?: string[]): string | undefined {
  if (!due?.overdue) return undefined;
  return focusOf({
    lead: `${month ? `${month} ` : ''}예정일 경과 ${due.overdue.toLocaleString('en-US')}건`, total: due.overdue, bySite: due.bySite,
    siteOwners: due.siteOwners, byOwner: due.byOwner, siteLabel: due.siteLabel, strict: true, drawn,
  })[0];
}

/** 두 달 판 쏠림 줄: 「9·10월 모두 미실시 38호기 중 가나본부 3개 현장 35호기(92.1%), 모두 A 담당」. */
function carryFocus(carry: CarryOver, group: CarryGroup, due: DueSplit | undefined): string[] {
  const number = (value: number) => value.toLocaleString('en-US');
  const unitWord = carry.unitLabel || '건';
  const total = group.units.length;
  const share = (count: number) => Math.round((count / total) * 1000) / 10;
  const owners = unitOwners(carry, group);
  const [owner, byOwner] = owners[0] ?? ['', 0];
  // 어느 달 배정인지 밝힌다. A는 9월에 14건을 했다(10차 검토: 「두 달 0건」은 틀린 문장).
  const idle = (name: string) => {
    const found = due?.idleOwners.find(([one]) => one === name);
    return found ? `(${carry.refName} 배정 ${number(found[1])}건 중 완료 0건)` : '';
  };
  const lead = `${group.label} ${number(total)}${unitWord}`;
  const family = unitFamily(unitSites(carry, group), total);
  const lines: string[] = [];
  if (family) {
    const familyOwners = unitOwners(carry, group, family.sites.reduce<string[]>((all, [, units]) => all.concat(units), []));
    const same = familyOwners.length === 1 && familyOwners[0]?.[0] === owner && byOwner === family.units;
    lines.push(`${lead} 중 ${family.family} ${family.sites.length}개 ${carry.siteLabel || '곳'} ${number(family.units)}${unitWord}`
      + `(${share(family.units)}%)${same ? `, 모두 ${owner} 담당${idle(owner)}` : ''}`);
    if (same) return lines;
  }
  if (owner && byOwner * 2 >= total) lines.push(`${lead} 중 ${owner} 담당 ${number(byOwner)}${unitWord}(${share(byOwner)}%)${idle(owner)}`);
  return lines;
}

/**
 * 담당은 셋까지, 나머지는 「외 N명(M건)」 — 9·10월 두 달 판에서 한 칸에 일곱 명이 실려 PDF 가 두 쪽이 됐다(2026-10-25).
 * 하나만 남으면 그 이름까지 쓴다. 호기로 센 묶음은 「외 3명(4호기)」.
 */
function fewCounts(pairs: [string, number][], unit = '건'): string {
  const number = (value: number) => value.toLocaleString('en-US');
  const shown = pairs.length === 4 ? pairs : pairs.slice(0, 3);
  const rest = pairs.slice(shown.length);
  const names = shown.map(([name, count]) => `${name} ${number(count)}`).join('·');
  return rest.length ? `${names} 외 ${rest.length}명(${number(rest.reduce((sum, [, count]) => sum + count, 0))}${unit})` : names;
}

function fewNames(names: string[]): string {
  return names.length > 4 ? `${names.slice(0, 3).join('·')} 외 ${names.length - 3}명` : names.join('·');
}

/** 오래 지난 경과분이 지난 날 수 — 「11~24일」, 예정일이 하루뿐이면 「11일」. */
function olderSpan(due: DueSplit): string {
  return due.olderMaxDays && due.olderMaxDays !== due.olderDays ? `${due.olderDays}~${due.olderMaxDays}일` : `${due.olderDays}일`;
}

/** 오래 지난 경과분의 예정일 — 「'26.10.1.~'26.10.14.」. */
function olderDates(due: DueSplit): string {
  return due.olderFirst && due.olderFirst !== due.olderLast ? `${due.olderFirst}~${due.olderLast}` : due.olderLast ?? '';
}

/** 직전 근무일분의 날짜 — 주말 예정분까지 묶었으면 범위(「'26.10.23.~'26.10.24.」). */
function recentDays(due: DueSplit): string {
  return due.recentLast ? `${due.recentDay}~${due.recentLast}` : due.recentDay ?? '';
}

/**
 * 「최근 예정분 8건」의 시점어를 날짜로 — 공문은 날짜로 쓴다(9차 검토). 규칙을 고친 뒤에도 06:53 모델 문장에 남았다.
 * 건수가 뒤따를 때만 바꾼다(「최근 3개월」은 그대로).
 */
export function withDates(lines: string[], due: DueSplit | undefined): string[] {
  const days = due?.recent ? recentDays(due) : '';
  return days ? lines.map((line) => line.replace(/(?:최근|직전\s*근무일)\s*예정분?(?=\s*\d[\d,]*\s*건)/g, `${days} 예정`)) : lines;
}

/**
 * 〈표 1〉 아래 줄 — 미실시 중 경과와 예정일 이내. 경과 외 377건이 문서에 없어 문제가 52건뿐인지 읽히지 않았다(5차 검토).
 */
export function dueNote(due: DueSplit | undefined): string | undefined {
  if (!due) return undefined;
  const within = due.today + due.upcoming;
  if (!within) return undefined;
  const number = (value: number) => value.toLocaleString('en-US');
  // 당일과 이후가 다 있을 때만 나눈다 — 「예정일 이내 336건(이후 336건)」은 같은 수의 되풀이다(2026-10-25).
  const parts = due.today && due.upcoming ? `(당일 ${number(due.today)}건, 이후 ${number(due.upcoming)}건)` : '';
  // 날짜가 달력에 없는 건은 따로 — 경과도 예정도 아니다(10차 검토: 원자료 점검일자 20260931).
  const invalid = due.invalid ? `날짜 오류 ${number(due.invalid)}건` : '';
  // 경과를 둘로 나눴으면 세 묶음을 그대로 쓴다. 「60건(8건 포함)」으로는 52가 안 보였다(9차 검토). 등호·더하기 대신 말로
  // 쓴다(12차 검토: 산문 속 수식기호, 사용자 전역 지침 stop-slop-ko).
  if (due.recent && due.olderLast && due.recent < due.overdue) {
    return `${due.attention} ${number(due.total)}건은 ${olderSpan(due)} 경과 ${number(due.overdue - due.recent)}건`
      + `, ${recentDays(due)} 예정분 ${number(due.recent)}건, 예정일 이내 ${number(within)}건${parts}${invalid ? `, ${invalid}` : ''}`;
  }
  return `${due.attention} ${number(due.total)}건 중 예정일 경과 ${number(due.overdue)}건, 예정일 이내 ${number(within)}건${parts}`
    + `${invalid ? `, ${invalid}` : ''}`;
}

/**
 * 확인 조치의 기한은 다음 근무일 — 「사유 확인」과 「점검 완료」가 같은 월말이면 상사가 「완료는 언제?」라고 묻는다(9차 검토).
 * 모델이 늦게 잡은 확인 기한만 당긴다.
 */
export function confirmFirst(actions: string[], basis: Date): string[] {
  const next = nextWorkday(basis);
  return actions.map((action) => action.replace(/(확인\s*\()'(\d{2})\.(\d{1,2})\.(\d{1,2})\.(?=까지)/, (whole, head: string, year: string, month: string, day: string) => (
    (2000 + Number(year)) * 10000 + Number(month) * 100 + Number(day) > dayNumber(next) ? `${head}${dayLabel(next)}` : whole)));
}

/**
 * 표가 있으면 기준일 대비 경과 근거 — 1턴 갈래 답도 보고서와 같은 것을 보게(9차 검토). 두 달 이상을 조회했으면
 * 경과는 기준월로 나누고 호기 묶음과 달별 몫을 함께 준다 — 「111건」에 9월분 51건이 든 줄 몰랐다(10차 검토).
 */
export function overdueFacts(table: TableData | undefined, basis: Date): string[] {
  if (!table) return [];
  const carry = carryOver(table, basis);
  const due = dueSplit(carry ? refTable(table, basis) : table, basis);
  // 달별 전체·완료·미실시와 날짜 오류도 준다(11차 검토). 1턴 답이 점검일자로 자른 갈래 값(10월 「1,949건 완료·60건
  // 계획」)을 보고서(완료 1,952건, 미실시 396건)와 같은 이름으로 썼고, 9월 1건 차이를 「집계 기준 차이」로 덮었다.
  // 실제 까닭은 없는 날짜('26.9.31.) 1건이었다.
  const counts = statusTable(table);
  const status = counts && carry ? { ...counts, table: monthStates(counts.table, basis, carry) } : counts;
  const broken = dateErrorNote(table);
  // 완료 점검 메모의 이상 기재도 준다(12차 검토: 1턴 답에도 긴급수리·통화 불능 메모 이야기가 없었다).
  const memo = memoLines(memoFindings(table), basis, monthScope(table, basis)?.ref);
  // 서명·미전송은 완료 건 중에서 센 것을 준다(16차 검토: 아직 안 한 점검의 미전송을 전송 누락처럼 썼다).
  const closing = closingLines(table, basis, monthScope(table, basis)?.ref);
  if (!due && !status && !memo && !closing.facts.length) return [];
  // 달별 미실시 현장 순위도 준다(실측 15:20: 현장별 전체 점검 건수를 미실시로 썼다).
  return [...reportFacts(status, undefined, due, basis, carry), ...siteFacts(siteCounts(table, basis)), ...(broken ? [broken] : []),
    ...(memo?.facts ?? []), ...closing.facts];
}

/**
 * 끝낸 뒤에 채우는 칸의 확인 줄(16차 검토). 정보센터 미전송 328건은 대부분 아직 실시하지 않은 점검(전송할 것이 없는 건)인데,
 * 1턴과 보고서 모두 전송 누락처럼 썼다. 서명처럼 끝낸 건 중에서만 센다. 보고서 서명 줄은 숫자 없는 「판단할 기준 자료 없음」으로
 * 돌아가기도 했다. 모집단의 「전송」·「고객싸인」 칸으로 코드가 쓴다. 서명은 기준월, 미전송은 달마다 완료 건이 있는 달만.
 * has는 그 칸이 있는지: 없으면 그 주제는 셀 수 없다.
 */
export function closingLines(table: TableData | undefined, basis: Date, ref?: string): {
  checks: { line: string; note: string }[]; facts: string[]; has: { sign: boolean; send: boolean };
  /** 완료 후 미전송과 「전송중」의 조치 줄(17차 검토: 확인 사항에만 있고 조치가 없었다). */
  actions: string[];
  /** ※ 줄: 전송 칸의 뜻 모를 값(확인 줄이 없을 때만). */
  notes: string[];
} {
  const sendAt = table ? table.columns.indexOf('전송') : -1;
  const signAt = table ? table.columns.findIndex((name) => /고객\s*(?:싸인|서명)/.test(name)) : -1;
  const has = { sign: signAt >= 0, send: sendAt >= 0 };
  const status = table?.rows.length ? statusColumn(table) : undefined;
  if (!table || !status || (!has.sign && !has.send)) return { checks: [], facts: [], has, actions: [], notes: [] };
  const number = (value: number) => value.toLocaleString('en-US');
  const month = monthColumn(table, status.index);
  const months = month >= 0 ? Array.from(new Set(table.rows.map((row) => monthKey(text(row[month]))))).filter(Boolean).sort() : [''];
  const latest = ref && months.indexOf(ref) >= 0 ? ref : months[months.length - 1] ?? '';
  const named = (key: string) => (key ? `${monthsText([key])} ` : '');
  const inMonth = (row: unknown[], key: string) => month < 0 || monthKey(text(row[month])) === key;
  const done = (row: unknown[]) => {
    const state = text(row[status.index]);
    return !!state && statusName(status, state) !== status.attention;
  };
  const owner = table.columns.findIndex((name) => OWNER.test(name));
  const site = nameColumn(table, [status.index, dateColumn(table), owner, month, sendAt, signAt]);
  const unitWord = table.columns.some((name, at) => at !== site && UNIT.test(name)) ? '호기' : '건';
  const checks: { line: string; note: string }[] = [];
  const facts: string[] = [];
  const actions: string[] = [];
  const notes: string[] = [];
  let signed = false;
  let sendNote = '';
  if (has.sign) {
    const missing = (rows: unknown[][]) => rows.filter((row) => !/^(?:있음|유|Y|O)$/i.test(text(row[signAt]))).length;
    months.forEach((key) => {
      const rows = table.rows.filter((row) => inMonth(row, key) && done(row));
      if (rows.length) facts.push(`${named(key)}완료 ${number(rows.length)}건 중 고객 서명 없음 ${number(missing(rows))}건`);
    });
    const rows = table.rows.filter((row) => inMonth(row, latest) && done(row));
    const gone = missing(rows);
    if (gone) {
      checks.push({
        line: `${named(latest)}완료 ${number(rows.length)}건 중 고객 서명 없음 ${number(gone)}건(${Math.round((gone / rows.length) * 1000) / 10}%), 필수 여부 확인 필요`,
        note: '서명(고객싸인) 기준',
      });
      signed = true;
    }
  }
  if (has.send) {
    const found: { key: string; rows: unknown[][] }[] = [];
    const odd: string[] = [];
    // 「전송중」: 정보센터 API가 바뀌기 전에는 0 미전송, 1 전송중(1시간쯤 뒤 반영을 확인해 2 전송완료로 바꿈)이었고, 지금은 0 미전송,
    // 2 전송완료뿐이다(사용자 설명, 2026-10-25). 그런데 10월 15~27일 점검에도 새로 찍혔고 13건 중 10건이 한 담당이었다(18차 검토).
    // 까닭은 자료로 알 수 없으니 짐작하지 않고 「완료 후 「전송중」에서 바뀌지 않은 건」으로, 각주가 아니라 확인 사항과 조치에 올린다.
    const stuck: { key: string; rows: unknown[][] }[] = [];
    months.forEach((key) => {
      const unsent = table.rows.filter((row) => inMonth(row, key) && text(row[sendAt]) === '미전송');
      const after = unsent.filter(done);
      // 뜻을 모르는 값(사전에 없는 코드)은 코드값을 드러내지 않고 따로 센다(17차 검토).
      const strange = table.rows.filter((row) => inMonth(row, key) && done(row) && !!text(row[sendAt])
        && !/^(?:미전송|전송중|전송|대상\s*아님)$/.test(text(row[sendAt]))).length;
      if (strange) odd.push(`${named(key)}${number(strange)}건`);
      const waiting = table.rows.filter((row) => inMonth(row, key) && done(row) && text(row[sendAt]) === '전송중');
      if (waiting.length) {
        stuck.push({ key, rows: waiting });
        const owners = owner >= 0 ? counted(waiting, owner) : [];
        facts.push(`${named(key)}완료 후 「전송중」에서 바뀌지 않은 건 ${number(waiting.length)}건${owners.length ? `(${fewCounts(owners)})` : ''}, `
          + '정보센터 반영 확인 필요');
      }
      const sites = site >= 0 ? counted(after, site).map(([name]) => name) : [];
      const list = sites.length ? `${sites.slice(0, 2).join('·')}${sites.length > 2 ? ' 등' : ''}` : '';
      // 괄호로 시작하는 현장 이름이면 괄호를 겹치지 않는다(실측 15:20: 「3건((가나)다라기술원)」).
      const shown = !list ? '' : list.startsWith('(') ? `, ${list}` : `(${list})`;
      if (unsent.length) {
        facts.push(`${named(key)}정보센터 미전송 ${number(unsent.length)}건 중 완료 후 미전송 ${number(after.length)}건${after.length ? shown : ''}, `
          + `나머지 ${number(unsent.length - after.length)}건은 아직 안 한 점검이라 전송할 것이 없다`);
      }
      if (after.length) found.push({ key, rows: after });
    });
    // 뜻 모를 값은 확인 줄 각주에 붙인다. ※ 줄을 따로 두면 한 장이 넘쳤다(실측 15:20 다시 그리기, 1,169px). 미전송 줄과 「전송중」
    // 줄은 각주가 같아 아래 줄 밑에 한 번만 붙는다.
    const note = `정보센터 전송 칸 기준, 완료 건만${odd.length ? `(전송 상태 미분류 완료 건 ${odd.join(', ')}, 뜻 확인 필요)` : ''}`;
    sendNote = note;
    if (found.length) {
      // 현장을 앞에 쓴다(17차 검토: 「(가나)다라기술원 3호기, 9월 완료 후 정보센터 미전송」). 두 달이면 현장마다 달을 붙인다.
      const single = found.length === 1 ? found[0] : undefined;
      const pieces = found.flatMap(({ key, rows }) => (site >= 0 ? counted(rows, site) : [['', rows.length] as [string, number]])
        .map(([name, count]) => ({ key, name, count }))).sort((left, right) => right.count - left.count);
      const label = pieces.slice(0, 2).map((one) => `${one.name ? `${one.name} ` : ''}${number(one.count)}${unitWord}`
        + `${single || !one.key ? '' : `(${monthsText([one.key])})`}`).join('·') + (pieces.length > 2 ? ` 외 ${pieces.length - 2}곳` : '');
      checks.push({ line: `${label}, ${single ? named(single.key) : ''}완료 후 정보센터 미전송`, note });
      const names = Array.from(new Set(pieces.map((one) => one.name).filter(Boolean)));
      const head = `${names.slice(0, 2).join('·')}${names.length > 2 ? ` 외 ${names.length - 2}곳` : ''}`;
      const rows = found.flatMap((one) => one.rows);
      const owners = owner >= 0 ? counted(rows, owner) : [];
      const what = single ? (single.key ? `${monthsText([single.key])}분 ` : '') : '완료 후 미전송 ';
      actions.push(`${head ? `${head} ` : ''}${what}${number(rows.length)}건 정보센터 전송 처리 확인(${dayLabel(nextWorkday(basis))}까지`
        + `${owners.length ? `, ${fewCounts(owners)}` : ''})`);
    }
    if (stuck.length) {
      const rows = stuck.flatMap((one) => one.rows);
      const owners = owner >= 0 ? counted(rows, owner) : [];
      const who = owners.length ? `, ${fewCounts(owners)}` : '';
      const single = stuck.length === 1 ? stuck[0] : undefined;
      const split = single ? '' : `(${stuck.map((one) => `${monthsText([one.key])} ${number(one.rows.length)}건`).join(', ')})`;
      // 두 달이면 근거에 합도 준다. 1턴이 더한 수를 스스로 지어내면 근거 밖 숫자가 된다.
      if (!single) facts.push(`${monthsText(stuck.map((one) => one.key))} 완료 후 「전송중」에서 바뀌지 않은 건 ${number(rows.length)}건${split}${who}`);
      checks.push({ line: `${single ? named(single.key) : ''}완료 후 「전송중」에서 바뀌지 않은 ${number(rows.length)}건${split}${who}`, note });
      actions.push(`「전송중」 ${number(rows.length)}건 정보센터 반영 확인(${dayLabel(nextWorkday(basis))}까지${who})`);
    }
    if (odd.length && !found.length && !stuck.length) notes.push(`정보센터 전송 상태 미분류 완료 건 ${odd.join(', ')}(뜻 확인 필요)`);
  }
  // 서명 줄과 전송 줄이 함께 있으면 각주 하나로 덮는다(모두 완료 건 중에서 센다). 각주 줄 하나 차이로 한 장이 두 장이 됐다
  // (실측 16:16을 18차 수정으로 다시 그리기, 원래 높이 1,159px).
  const noted = signed && checks.length > 1 ? checks.map((one) => ({ ...one, note: `고객싸인·${sendNote}` })) : checks;
  return { checks: noted, facts, has, actions, notes };
}

/**
 * 달마다 현장별 미실시와 예정일 경과 수(names는 모집단의 모든 현장 이름). 1턴 답이 현장별 전체 점검 건수(가나문화전당
 * 50건, 모두 완료)를 「미실시가 많은 현장」으로 썼다(실측 15:20). 1턴 글의 「현장 N건」을 이것으로 대조하고, 근거에도 순위를 준다.
 */
export function siteCounts(table: TableData | undefined, basis: Date): {
  names: string[]; months: { month: string; all: [string, number][]; late: [string, number][] }[];
  /** 담당자별(실측 16:16: 순위 문장이 위의 A·B를 뺐다). */
  owners?: { names: string[]; months: { month: string; all: [string, number][]; late: [string, number][] }[] };
} | undefined {
  const status = table?.rows.length ? statusColumn(table) : undefined;
  if (!table || !status) return undefined;
  const month = monthColumn(table, status.index);
  const dateAt = dateColumn(table);
  const owner = table.columns.findIndex((name) => OWNER.test(name));
  const site = nameColumn(table, [status.index, dateAt, owner, month]);
  if (site < 0) return undefined;
  const today = dayNumber(basis);
  const keys = month >= 0 ? Array.from(new Set(table.rows.map((row) => monthKey(text(row[month]))))).filter(Boolean).sort() : [''];
  const split = keys.map((key) => {
    const rows = table.rows.filter((row) => (month < 0 || monthKey(text(row[month])) === key)
      && statusName(status, row[status.index]) === status.attention);
    const late = dateAt >= 0 ? rows.filter((row) => {
      const day = calendarDay(text(row[dateAt]));
      return day > 0 && day < today;
    }) : [];
    return { month: key ? monthsText([key]) : '', rows, late };
  });
  const by = (index: number) => split.map((one) => ({ month: one.month, all: counted(one.rows, index), late: counted(one.late, index) }));
  return {
    names: Array.from(new Set(table.rows.map((row) => text(row[site])).filter(Boolean))),
    months: by(site),
    ...(owner >= 0 ? { owners: { names: Array.from(new Set(table.rows.map((row) => text(row[owner])).filter(Boolean))), months: by(owner) } } : {}),
  };
}

/** 근거로 주는 달별 미실시 현장 순위: 여덟 곳까지, 끝이 동점이면 셋까지 더 싣는다. */
function siteFacts(counts: ReturnType<typeof siteCounts>): string[] {
  const number = (value: number) => value.toLocaleString('en-US');
  return (counts?.months ?? []).flatMap((one) => {
    const total = one.all.reduce((sum, [, count]) => sum + count, 0);
    if (!total) return [];
    let limit = Math.min(8, one.all.length);
    while (limit < one.all.length && limit < 11 && one.all[limit]?.[1] === one.all[limit - 1]?.[1]) limit += 1;
    return [`${one.month ? `${one.month} ` : ''}미실시 ${number(total)}건 현장별: `
      + one.all.slice(0, limit).map(([name, count]) => `${name} ${number(count)}`).join(', ')];
  });
}

/**
 * 기준월 예정일 경과 전체의 예정일 범위(YYYYMMDD). 1턴 답이 「10월만 경과」 묶음의 범위를 경과 전체의 범위로 썼다(17차 검토).
 */
export function overdueSpan(table: TableData | undefined, basis: Date): { month: string; first: number; last: number } | undefined {
  if (!table) return undefined;
  const scope = monthScope(table, basis);
  const current = scope ? refTable(table, basis) : table;
  const status = current.rows.length ? statusColumn(current) : undefined;
  const dateAt = dateColumn(current);
  if (!status || dateAt < 0) return undefined;
  const today = dayNumber(basis);
  const days = current.rows.filter((row) => statusName(status, row[status.index]) === status.attention)
    .map((row) => calendarDay(text(row[dateAt]))).filter((day) => day > 0 && day < today);
  return days.length ? { month: scope ? monthsText([scope.ref]) : '', first: Math.min(...days), last: Math.max(...days) } : undefined;
}

/** 제목이 달로 시작하고 해가 없으면 기준월의 해를 붙인다(16차 검토: 「10월 점검 실적 및 미실시 현황」). */
export function titleWithYear(title: string, ref: string | undefined, basis: Date): string {
  if (!/^\s*\d{1,2}\s*월/.test(title) || /(?:19|20)\d{2}\s*년/.test(title)) return title;
  return `${ref ? ref.slice(0, 4) : basis.getFullYear()}년 ${title.trim()}`;
}

/**
 * 두 달 판 〈표 2〉 — 묶음마다 호기·건수·기준월 예정일·담당, 첫 묶음은 현장별(쏠림 줄의 이름 묶음 현장을 먼저).
 * 계는 「73호기·111건」으로 호기와 건을 가른다(10차 검토). 건수 칸 이름은 「경과 건수」: 「9월 미실시·10월 예정」의 13건은
 * 9월분뿐이고 10월 예정분은 예정일 이내에 든다. 「건수」로 두면 호기마다 1건만 남은 것으로 읽혔다(11차 검토).
 */
function carryRows(table: TableData, basis: Date, carry: CarryOver, crowded = false, bare = false): ExceptionTable {
  const status = statusColumn(table);
  const unitWord = carry.unitLabel || '건';
  const withUnits = carry.unit >= 0;
  const cells = (name: string, units: number, rows: unknown[][], refRows: unknown[][], owners: string) => [
    name, ...(withUnits ? [units] : []), rows.length, dayRange(refRows, carry.dateIndex), ...(carry.owner >= 0 ? [owners] : [])];
  const rows: unknown[][] = [];
  carry.groups.forEach((group, at) => {
    rows.push(cells(`${group.label} 소계`, group.units.length, group.rows, group.refRows, fewCounts(unitOwners(carry, group), unitWord)));
    // 더 붐비는 판(메모 줄에 서명·미전송 확인 줄까지)은 첫 묶음의 현장 줄을 빼고 소계만 남긴다. 바로 위 쏠림 줄이 같은 말을 한다
    // (실측 15:20 다시 그리기: 1,154px로 쪽 맞춤 한도를 넘었다, 12차 검토 차례: 〈표 2〉 현장 줄부터 줄인다).
    if (at > 0 || carry.site < 0 || bare) return;
    // 첫 묶음은 현장별로 싣는다. 쏠림 줄이 말한 이름 묶음(가나본부) 현장을 먼저 다 싣고, 나머지는 「그 밖」.
    const sites = unitSites(carry, group);
    const family = unitFamily(sites, group.units.length);
    // 메모 확인 줄이 들어가 한 쪽이 넘치면 이름 묶음 현장을 한 줄로 묶는다(12차 검토: 넘치면 〈표 2〉 현장 줄부터 줄인다).
    // 쏠림 줄이 이미 「가나본부 3개 현장 35호기」라 낱 현장 줄이 없어도 읽힌다(실측 11:56, 1,185px).
    const merged: [string, string[]] | undefined = crowded && family
      ? [`${family.family} ${family.sites.length}개 ${carry.siteLabel || '곳'}`, family.sites.reduce<string[]>((all, [, one]) => all.concat(one), [])]
      : undefined;
    const others = family ? sites.filter((entry) => family.sites.indexOf(entry) < 0) : sites;
    const ordered = merged ? [merged, ...others] : family ? [...family.sites, ...others] : sites;
    const limit = merged ? 1 : family ? Math.min(family.sites.length, 5) : crowded ? 2 : 4;
    const shown = ordered.length === limit + 1 ? ordered : ordered.slice(0, limit);
    const rest = ordered.slice(shown.length);
    const pick = (units: string[], from: unknown[][]) => from.filter((row) => units.indexOf(carry.unitOf(row)) >= 0);
    shown.forEach(([name, units]) => {
      const names = Array.from(new Set(unitOwners(carry, group, units).map(([owner]) => owner)));
      rows.push(cells(name, units.length, pick(units, group.rows), pick(units, group.refRows), fewNames(names)));
    });
    if (rest.length) {
      const units = rest.reduce<string[]>((all, [, one]) => all.concat(one), []);
      rows.push(cells(`그 밖 ${rest.length}개 ${carry.siteLabel || '곳'}`, units.length, pick(units, group.rows), pick(units, group.refRows),
        fewCounts(unitOwners(carry, group, units), unitWord)));
    }
  });
  const raw = status ? status.values.find((value) => plainStatus(value, status.values) === status.attention) ?? status.attention : '';
  const statusName = status ? table.columns[status.index] ?? '' : '';
  return {
    title: `${carry.attention} ${withUnits ? unitWord : ''} 구분`.replace(/\s+/g, ' '),
    unit: withUnits ? `${unitWord}, 건` : '건',
    table: {
      columns: [carry.site >= 0 ? table.columns[carry.site] ?? '' : '구분', ...(withUnits ? [unitWord] : []), '경과 건수',
        `${carry.refName} 예정일`, ...(carry.owner >= 0 ? [table.columns[carry.owner] ?? ''] : [])],
      rows: [...rows, ['계', ...(withUnits ? [carry.units] : []), carry.rows, '', ...(carry.owner >= 0 ? [''] : [])]],
    },
    total: carry.rows,
    listed: carry.rows,
    ...(withUnits ? { units: carry.units } : {}),
    // ※ 판정은 짧게: 「결과 「계획」은 미실시」가 전월분도 말한다. 호기를 무엇으로 셌는지만 더한다.
    // 호기를 무엇으로 셌는지는 현장 칸이 있을 때만 쓴다. 없으면 「호기 = ·호기 기준」으로 깨졌다(코드 리뷰).
    // 「=」와 「 · 」 나열 대신 「~는」과 쉼표(사용자 전역 지침 stop-slop-ko).
    rule: `예정일 경과는 ${statusName} 「${raw}」 중 예정일이 기준일(${dayLabel(basis)}) 이전`
      + (withUnits && carry.site >= 0
        ? `, ${topicParticle(unitWord)} ${table.columns[carry.site] ?? ''}·${table.columns[carry.unit] ?? ''} 기준` : ''),
  };
}

/**
 * 〈표 2〉 예정일 경과 건 — 이름(현장)별로 묶어 건수·예정일(범위)·담당. 낱건 다섯 줄보다 상사가 볼 것이 이것이다.
 */
export type ExceptionTable = {
  title: string; table: TableData; total: number; listed: number; rule: string;
  /** 표의 단위 — 두 달 판은 「호기, 건」. */
  unit?: string;
  /** 두 달 판의 호기 수(73호기). total 은 건수(111건). */
  units?: number;
};

export function exceptionRows(table: TableData, basis: Date, crowded = false, bare = false): ExceptionTable | undefined {
  const status = statusColumn(table);
  if (!status) return undefined;
  // 두 달 이상을 조회했으면 호기 묶음 — 같은 호기를 두 번 센 「11~54일 경과 103건」이 반려됐다(10차 검토). 묶음이
  // 없으면 〈표 2〉도 없다 — 「계 0 0」 한 줄짜리 표가 나왔다(코드 리뷰).
  const carry = carryOver(table, basis);
  if (carry) return carry.groups.length ? carryRows(table, basis, carry, crowded, bare) : undefined;
  const due = dueSplit(table, basis);
  if (!due || !due.overdue) return undefined;
  const dateIndex = dateColumn(table);
  const owner = table.columns.findIndex((column) => OWNER.test(column));
  const site = nameColumn(table, [status.index, dateIndex, owner]);
  const today = dayNumber(basis);
  const dayOf = (row: unknown[]) => calendarDay(text(row[dateIndex]));
  const late = table.rows.filter((row) => plainStatus(text(row[status.index]), status.values) === status.attention
    && dayOf(row) > 0 && dayOf(row) < today);
  // 「그 밖」 줄의 담당은 건수 순으로 건수까지 쓴다. 가장 많은 E 5건이 이름에서 빠졌다(10차 검토).
  const line = (key: string, members: unknown[][], tally = false) => {
    const days = members.map((row) => text(row[dateIndex]).replace(/-/g, '')).sort();
    const first = shortDate(days[0] ?? '');
    const lastDay = shortDate(days[days.length - 1] ?? '');
    const owners = owner < 0 ? '' : tally ? fewCounts(counted(members, owner))
      : fewNames(Array.from(new Set(members.map((row) => text(row[owner])).filter(Boolean))));
    return [key, members.length, first === lastDay ? first : `${first}~${lastDay}`, ...(owner >= 0 ? [owners] : [])];
  };
  const label = site >= 0 ? (table.columns[site] ?? '').replace(/명$/, '') : '';
  // 이름(현장)별 줄. 나머지는 「그 밖 N개 현장」 한 줄 — 보이는 행의 합이 계와 맞지 않았다(6차 검토).
  // 하나만 남으면 그 이름을 쓴다 — 「그 밖 1개 현장」은 이름 하나를 감춘다.
  const block = (members: unknown[][], limit: number) => {
    const groups = new Map<string, unknown[][]>();
    members.forEach((row) => {
      const key = site >= 0 ? text(row[site]) : '';
      groups.set(key, [...(groups.get(key) ?? []), row]);
    });
    const sorted = Array.from(groups.entries()).sort((left, right) => right[1].length - left[1].length);
    const shown = sorted.length === limit + 1 ? sorted : sorted.slice(0, limit);
    const rest = sorted.slice(shown.length);
    return [
      ...shown.map(([key, rows]) => line(key, rows)),
      ...(rest.length ? [line(`그 밖 ${rest.length}개 ${label || '곳'}`, rest.reduce<unknown[][]>((all, [, rows]) => all.concat(rows), []), true)] : []),
    ];
  };
  // 경과를 오래 지난 것과 직전 근무일분으로 나눴으면 표도 소계로 나눈다 — 「그 밖」 행에 둘이 섞여, 52를 맞춰 보려면
  // 예정일을 하나씩 세어야 했다(8차 검토).
  const workday = dayNumber(previousWorkday(basis));
  const recent = late.filter((row) => dayOf(row) >= workday);
  const older = late.filter((row) => dayOf(row) < workday);
  const subtotal = (title: string, members: unknown[][], dates: string) => [`${title} 소계`, members.length, dates,
    ...(owner >= 0 ? [fewCounts(counted(members, owner))] : [])];
  const parts: { head?: unknown[]; rows: unknown[][] }[] = recent.length && older.length && due.olderLast
    ? [{ head: subtotal(`${olderSpan(due)} 경과`, older, olderDates(due)), rows: block(older, SPLIT_OLDER) },
      { head: subtotal(`${recentDays(due)} 예정분`, recent, ''), rows: [] }]
    : [{ rows: block(late, MAX_EXCEPTIONS) }];
  const rows = parts.flatMap((part) => [...(part.head ? [part.head] : []), ...part.rows]);
  // 접은 묶음은 소계가 그 건수를 보인다 — 「외 N건」으로 세지 않는다.
  const listed = parts.reduce((sum, part) => sum + (part.rows.length
    ? part.rows.reduce((inner, row) => inner + (row[1] as number), 0) : Number(part.head?.[1] ?? 0)), 0);
  const raw = status.values.find((value) => plainStatus(value, status.values) === status.attention) ?? status.attention;
  return {
    title: `예정일 경과 ${status.attention}`,
    table: {
      // 날짜 열은 무엇의 날짜인지로 — 「점검일자」가 아니라 「예정일」(3차 검토). 끝에 「계」 행.
      columns: [site >= 0 ? table.columns[site] ?? '' : '구분', '건수', '예정일', ...(owner >= 0 ? [table.columns[owner] ?? ''] : [])],
      rows: [...rows, ['계', late.length, '', ...(owner >= 0 ? [''] : [])]],
    },
    total: late.length,
    // 「외 N건」은 실은 이름들의 건수 합으로 센다 — 「계」 행은 넣지 않는다.
    listed,
    rule: `예정일 경과는 ${table.columns[status.index]} 「${raw}」 중 예정일이 기준일(${dayLabel(basis)}) 이전`,
  };
}

/**
 * 완료 점검 메모(비고)의 이상 기재 묶음, 무거운 차례. 이 회사 승강기 점검 메모의 낱말이다(12차 검토).
 * 긴급수리는 「C(긴급수리)로 표기함」만 센다: 「…표기하게 되어있으나 경미하여 B(주의관찰)를 유지함」은 B다.
 */
const MEMO_FAMILIES = [
  { key: 'urgent', name: '긴급수리', test: /C\s*\(\s*긴급\s*수리\s*\)\s*로\s*표기함|C\s*등급/ },
  // 13차 검토로 낱말을 더했다: 「ARD」(자동구출운전장치), 「구출」(「지동구출배터리」처럼 운전 없이), 「비통」(비상통화 줄임말),
  // 「국선」(비상통화 선로), 「멀티빔」(문 안전장치). 같은 성격의 미해결 메모 7곳이 빠져 있었다.
  // 14차 검토로 더했다: 「ELD」(구출운전 장치)와 소문자 「ard」, 비상통화 외부 회선 「유심」, 「인한 정지」(침수·누수로 멈춤),
  // 「파단」(카도어 연동로프). 「정지층」(층 맞춤)과 「비상정지 스위치」는 이상이 아니라 「정지」만으로는 걸지 않는다.
  { key: 'rescue', name: '구출운전', test: /구출|\b(?:ARD|ELD)\b/i },
  { key: 'phone', name: '비상통화장치', test: /비상\s*통화|비통|국선|유심|통화\s*(?:연결|장치|선로)|인터폰|수신\s*감도/ },
  { key: 'stop', name: '고장', test: /인한\s*정지|정지\s*(?:상태|됨|중)|운행\s*정지|작동\s*불능|모터\s*고장|멀티\s*빔|파단/ },
] as const;

/**
 * 긴급수리가 아닌 메모는 아직 고치지 않은 문제만 센다. 실측 11:43 원자료에는 고친 기록(「비상통화장치 보완완료」,
 * 「인터폰 교체」, 「배터리교체함」)이 많아 10월이 24개 현장으로 부풀었다. 문제 낱말이나 예정 낱말이 있어야 하고,
 * 끝낸 기록뿐이면(예정 낱말 없이 완료·교체함·양호) 세지 않는다.
 */
const MEMO_PROBLEM = /불능|불량|안\s*됨|되지\s*않|이상(?!\s*없)|방전|약함|저전압|노이즈|미점등|소손|동작\s*불가|작동\s*불가|부족|끊김|끊어(?:짐|져|졌)|단선|해지|폐지|노후|인한\s*정지|정지\s*(?:상태|됨|중)|파단|고장|우려/;
// 아직 고치지 않은 일: 「재신청중」, 「신청상태」, 「협의중」, 「조치하기로 함」(14차 검토).
const MEMO_PENDING = /예정|요함|요청|재신청|신청\s*(?:중|상태)|협의\s*중|조치하기로/;
// 「… 노후화로 판단되어 교체」처럼 교체로 끝난 문장은 이미 고친 기록이다(13차 검토).
const MEMO_DONE = /완료|교체함|교체\s*진행|정상\s*작동|양호|이상\s*없|교체\s*$/;

export type MemoFinding = {
  family: (typeof MEMO_FAMILIES)[number]['key'];
  name: string;
  site: string;
  /** 그 메모가 붙은 달(YYYYMM, 달 칸이 없으면 빈 글자), 차례대로. */
  months: string[];
  /** 마지막 달에 그 메모가 붙은 호기 수. 행이 아니라 호기로 센다. */
  units: number;
  owners: string[];
  /** 메모에서 그 낱말이 든 마디(「102동1호기 수동구출운전 작동불능」). 긴급수리는 「C(긴급수리) 표기」. */
  clause: string;
  /** 가장 늦은 달의 메모 원문(줄바꿈은 한 칸으로). 보고서 데이터만으로 검산하게 남긴다(13차 검토). */
  memo: string;
  /** 가장 늦은 달의 담당별 호기 수. 조치 줄 담당을 호기 수 차례로 쓴다(14차 검토). */
  ownerUnits: [string, number][];
};

/**
 * 완료 점검 메모의 이상 기재. 판정 칸이 없어도 메모에 긴급수리·고장이 적혀 있으면 「판단 불가」로만 올리지 않는다
 * (국가직 팀장 12차 검토: 가나아파트 1단지 39호기 「C(긴급수리)로 표기함」 두 달 연속, 10월 비상통화장치·구출운전
 * 이상 5개 현장을 다루지 않아 반려). 메모는 판정 항목이 아니다. 행이 아니라 호기·현장으로 센다: 가나갤러리303은 3개
 * 호기를 적은 메모 하나가 6행에 똑같이 붙어 있었다. 과적주의·에어컨 콤프 불량 같은 가벼운 메모와 아직 안 한 행은 세지 않는다.
 */
export function memoFindings(table: TableData | undefined): MemoFinding[] {
  if (!table?.rows.length) return [];
  const remark = table.columns.findIndex((name) => /비고|메모|remark|특이/i.test(name));
  const status = statusColumn(table);
  if (remark < 0 || !status) return [];
  const month = monthColumn(table);
  const owner = table.columns.findIndex((name) => OWNER.test(name));
  const site = nameColumn(table, [status.index, remark, owner, dateColumn(table), month]);
  const unit = table.columns.findIndex((name, at) => at !== site && UNIT.test(name));
  type Entry = {
    family: MemoFinding['family']; name: string; site: string; months: Map<string, Set<string>>; owners: string[];
    clause: string; clauseMonth: string; memo: string; owned: Map<string, Map<string, Set<string>>>;
  };
  const found = new Map<string, Entry>();
  table.rows.forEach((row, at) => {
    const state = text(row[status.index]);
    if (!state || statusName(status, state) === status.attention) return;
    // 문장(줄바꿈·마침표)마다 본다. 한 메모에 고친 구출운전과 다른 문제(인디게이터 불량·견적예정)가 줄을 나눠 적혀
    // 구출운전 문제로 잘못 셌다(실측 11:43). 쉼표로 늘어놓은 한 문장은 함께 본다(「비상통화, 비상등 미점등으로 … 교체 예정」).
    const sentences = text(row[remark]).split(/[.\n]+/).map((one) => one.replace(/\s+/g, ' ').trim()).filter(Boolean);
    const family = MEMO_FAMILIES.find((one) => sentences.some((sentence) => one.test.test(sentence)));
    const sentence = family ? sentences.find((one) => family.test.test(one)) ?? '' : '';
    if (!family || !sentence) return;
    // 고친 기록·예정 낱말은 그 장치 낱말이 든 마디부터 본다. 앞 마디의 「양호」는 다른 장치(조속기) 말이었다(14차 검토:
    // 「추락방지장치(조속기) 동작상태 양호, 1호기 카도어 연동로프 파단」을 고친 기록으로 뺐다). 문제 뒤의 「교체함」은 고친 것이다.
    const parts = sentence.split(/,\s*/);
    const tail = parts.slice(Math.max(0, parts.findIndex((one) => family.test.test(one)))).join(', ');
    const pending = MEMO_PENDING.test(tail);
    if (family.key !== 'urgent' && (!(MEMO_PROBLEM.test(sentence) || pending) || (MEMO_DONE.test(tail) && !pending))) return;
    const where = site >= 0 ? text(row[site]) : '';
    const key = `${family.key}\u0001${where}`;
    const when = month >= 0 ? monthKey(text(row[month])) : '';
    const clause = family.key === 'urgent' ? 'C(긴급수리) 표기' : memoClause(sentence, family.test);
    const entry: Entry = found.get(key) ?? {
      family: family.key, name: family.name, site: where, months: new Map<string, Set<string>>(), owners: [], clause, clauseMonth: when,
      memo: '', owned: new Map<string, Map<string, Set<string>>>(),
    };
    // 보여 줄 마디는 가장 늦은 달 메모에서(9월 「…방전으로 동작 불능」보다 10월 「…교체 예정」이 지금 상태다).
    if (when > entry.clauseMonth) {
      entry.clause = clause;
      entry.clauseMonth = when;
      entry.memo = '';
    }
    if (!entry.memo && when === entry.clauseMonth) entry.memo = text(row[remark]).replace(/\s+/g, ' ').trim();
    const unitKey = unit >= 0 ? text(row[unit]) : `#${at}`;
    const units = entry.months.get(when) ?? new Set<string>();
    units.add(unitKey);
    entry.months.set(when, units);
    const name = owner >= 0 ? text(row[owner]) : '';
    if (name && entry.owners.indexOf(name) < 0) entry.owners.push(name);
    if (name) {
      const owned = entry.owned.get(when) ?? new Map<string, Set<string>>();
      owned.set(name, (owned.get(name) ?? new Set<string>()).add(unitKey));
      entry.owned.set(when, owned);
    }
    found.set(key, entry);
  });
  const order: string[] = MEMO_FAMILIES.map((one) => one.key);
  return Array.from(found.values()).map((entry) => {
    const months = Array.from(entry.months.keys()).sort();
    const last = months[months.length - 1] ?? '';
    return {
      family: entry.family, name: entry.name, site: entry.site, months,
      units: entry.months.get(last)?.size ?? 0, owners: entry.owners, clause: entry.clause, memo: entry.memo,
      ownerUnits: Array.from((entry.owned.get(last) ?? new Map<string, Set<string>>()).entries())
        .map(([name, set]) => [name, set.size] as [string, number]),
    };
  }).sort((left, right) => order.indexOf(left.family) - order.indexOf(right.family) || right.units - left.units);
}

/**
 * 메모에서 그 낱말이 든 쉼표 마디. 그 마디에 문제·예정 낱말이 없으면 뒤 마디를 이어 붙인다: 「비상통화장치 카상부」만으로는
 * 「외부수신감도불량」이 안 보였다(실측 11:43). 길면 앞 36자만.
 */
function memoClause(memo: string, test: RegExp): string {
  const parts = memo.split(/,\s*/).map((one) => one.trim()).filter(Boolean);
  const from = Math.max(0, parts.findIndex((one) => test.test(one)));
  const told = parts.slice(from).findIndex((one) => MEMO_PROBLEM.test(one) || MEMO_PENDING.test(one));
  // 장치 낱말 앞에 쉼표로 이어진 호기 이름은 같은 마디로 본다. 「101동1호기, 102동1호기 수동구출운전 작동불능」에서
  // 「101동1호기」가 떨어져 한 호기만 고장 난 것처럼 읽혔다(15차 검토).
  let start = from;
  while (start > 0 && UNIT_NAME.test(parts[start - 1] ?? '')) start -= 1;
  const part = parts.slice(start, from + Math.max(told, 0) + 1).join(', ');
  if (part.length <= 36) return part;
  // 길면 호기 이름을 호기 수로 줄인다(「3개 호기 자동구출운전 배터리 방전됨」).
  const names = part.match(UNIT_NAMES) ?? [];
  const rest = part.replace(UNIT_NAMES, '').replace(/^[,\s]+/, '').replace(/\s*,\s*,/g, ',').trim();
  const short = names.length > 1 ? `${new Set(names).size}개 호기 ${rest}` : part;
  return short.length > 36 ? short.slice(0, 36).trim() : short;
}

/** 호기 이름만 있는 마디(「101동1호기」, 「EL01호기」)와, 글 속 호기 이름(뒤 쉼표까지). */
const UNIT_NAME = /^[\w가-힣()-]*\d+\s*호기$/;
const UNIT_NAMES = /[\w가-힣()-]*\d+\s*호기\s*,?\s*/g;

/** 메모를 말한 줄. */
const MEMO_TALK = /긴급\s*수리|구출\s*운전|비상\s*통화|메모|비고/;

/**
 * 요약에 메모 줄을 싣는다(12차 검토). 자리는 「판단 불가」 줄, 메모를 말한 줄, 셋째 줄 차례다. 모델이 셋째 줄을 다른 말로
 * 써서 메모 줄(긴급수리 39호기 두 달 연속)이 요약에서 빠졌다(실측 14:47). 요약은 세 줄까지다.
 */
export function withMemoSummary(summary: string[], memo?: string): string[] {
  if (!memo) return summary;
  const at = [summary.findIndex((line) => /판단\s*불가/.test(line)), summary.findIndex((line) => MEMO_TALK.test(line))]
    .find((index) => index >= 0);
  if (at !== undefined) return summary.map((line, index) => (index === at ? memo : line));
  return summary.length < 3 ? [...summary, memo] : [...summary.slice(0, 2), memo];
}

/**
 * 메모 확인의 문서 줄(12차 검토). 요약은 「판단 불가」 대신 「판정 항목은 없으나 … 기재 확인 필요」, 확인 필요 사항은
 * 긴급수리 한 줄과 기준월의 다른 이상 한 줄(그 아래 각주), 향후 조치는 한 줄(기한은 다음 근무일), 그리고 모델에게 줄 근거.
 * ref는 기준월(YYYYMM). 기준월이 아닌 달의 이상은 근거에만 둔다(긴급수리는 달과 상관없이 싣는다).
 * tight는 더 붐비는 판(〈표 2〉 묶음 넷 이상)이다: 요약의 「두 달 연속」은 바로 아래 확인 줄이 같은 말을 하니 빼고, 확인 줄의
 * 보기는 하나만 든다(실측 2026-10-26 08:49: 원자료가 바뀌어 묶음이 넷이 되자 1,174px로 두 장이 됐다).
 */
export function memoLines(findings: MemoFinding[], basis: Date, ref?: string, tight = false): {
  summary: string; checks: string[]; note: string; action?: string; facts: string[];
} | undefined {
  if (!findings.length) return undefined;
  const latest = ref ?? findings.flatMap((one) => one.months).sort().pop() ?? '';
  const refName = latest ? monthsText([latest]) : '';
  const urgent = findings.filter((one) => one.family === 'urgent');
  const others = findings.filter((one) => one.family !== 'urgent');
  const recent = others.filter((one) => one.months.indexOf(latest) >= 0);
  const older = others.filter((one) => one.months.indexOf(latest) < 0);
  const spanOf = (one: MemoFinding) => {
    const months = one.months.filter(Boolean);
    return months.length > 1 ? `${monthsText(months)} 연속` : months.length ? monthsText(months) : '';
  };
  const namesOf = (list: MemoFinding[]) => Array.from(new Set(list.map((one) => one.name))).join('·');
  const sitesOf = (list: MemoFinding[]) => Array.from(new Set(list.map((one) => one.site)));
  const shown = recent.length || urgent.length ? recent : older;
  // 두 달에 걸친 현장: 묶음이 달라도 같은 현장이면 함께 센다(가나중학교는 9월 구출운전·수신감도, 10월 비상통화). 같은 이상이
  // 두 번의 월 점검 동안 그대로 남은 것이 이 줄에서 가장 무거운 사실이다(14차 검토).
  const months = Array.from(new Set(others.flatMap((one) => one.months))).filter(Boolean).sort();
  const prev = months.filter((month) => month < latest).pop() ?? '';
  const recentSites = sitesOf(recent);
  const prevSites = prev ? sitesOf(others.filter((one) => one.months.indexOf(prev) >= 0)) : [];
  const both = recentSites.filter((site) => prevSites.indexOf(site) >= 0);
  const head = urgent[0]
    ? `${urgent[0].site} ${urgent[0].units}호기 C(긴급수리)${!tight && urgent[0].months.filter(Boolean).length > 1 ? ' 두 달 연속' : ''}`
    : `${shown[0]?.site ?? ''} ${shown[0]?.clause ?? ''}`.trim();
  const label = [urgent.length ? '긴급수리' : '', others.length ? '고장' : ''].filter(Boolean).join('·');
  const checks: string[] = [];
  if (urgent[0]) {
    const span = spanOf(urgent[0]);
    // 확인 사항에는 사실만 쓴다. 확인 행위는 조치 줄에 있다(13차 검토: 같은 말을 두 번 썼다).
    checks.push(`${urgent[0].site} ${urgent[0].units}호기${urgent.length > 1 ? ` 외 ${urgent.length - 1}개 현장` : ''} C(긴급수리) 메모`
      + `${span ? ` ${span}` : ''}`);
  }
  if (shown.length) {
    const sites = sitesOf(shown);
    const when = shown === recent ? refName : monthsText(Array.from(new Set(shown.flatMap((one) => one.months))).filter(Boolean).sort());
    const repeated = shown === recent ? both : [];
    const examples = (list: string[]) => {
      const count = tight ? 1 : 2;
      return `${list.slice(0, count).join('·')}${list.length > count ? ' 등' : ''}`;
    };
    checks.push(repeated.length
      ? `${when ? `${when} ` : ''}${namesOf(shown)} 이상 메모 ${sites.length}개 현장, 이 중 ${repeated.length}곳은 ${monthsText([prev])}에도 이상 기재`
        + `(${examples(repeated)})`
      : `${when ? `${when} ` : ''}${namesOf(shown)} 이상 메모 ${sites.length}개 현장(${examples(sites)})`);
  }
  // 붐비는 판의 조치 줄은 이상 종류를 바로 위 확인 줄에 맡긴다.
  const parts = [
    urgent.length ? `긴급수리 표기 ${urgent.reduce((sum, one) => sum + one.units, 0).toLocaleString('en-US')}호기 수리 계획` : '',
    shown.length ? `${tight ? '이상 메모' : `${namesOf(shown)} 이상`} ${sitesOf(shown).length}개 현장 조치 여부` : '',
  ].filter(Boolean);
  // 담당은 기준월 메모 호기 수가 많은 차례로(14차 검토: 「G·H·I」는 건수 순이 아니었다).
  const owned = new Map<string, number>();
  [...urgent, ...shown].forEach((one) => one.ownerUnits.forEach(([name, units]) => owned.set(name, (owned.get(name) ?? 0) + units)));
  const owners = Array.from(owned.entries()).sort((left, right) => right[1] - left[1]).map(([name]) => name);
  // 지난 달 근거는 그 달 현장 전부를 센다. 그 달에만 있던 현장만 세어 1턴 답이 「9월에도 20개 현장」(실제 26곳)이라 했다(14차 검토).
  const olderMonths = months.filter((month) => month !== latest);
  const facts = [
    '완료 점검 메모(비고)의 이상 기재는 판정 항목이 아니라 메모 문구다. 행이 아니라 호기·현장으로 센다(같은 메모가 여러 행에 붙는다). '
      + '메모가 붙은 행 수를 이상 건수로 쓰지 않는다',
    ...urgent.map((one) => `긴급수리 C 표기 메모: ${one.site} ${one.units}호기(${spanOf(one) || '이번 조회'}`
      + `${one.owners.length ? `, ${fewNames(one.owners)} 담당` : ''})`),
    ...(recent.length ? [`${refName ? `${refName} ` : ''}메모 이상 ${recentSites.length}개 현장`
      + `${both.length ? `(이 중 ${both.length}곳은 ${monthsText([prev])}에도 기재)` : ''}: `
      + `${recent.map((one) => `${one.site}(${one.clause})`).join(', ')}`] : []),
    ...olderMonths.map((when) => {
      const list = others.filter((one) => one.months.indexOf(when) >= 0);
      const only = list.filter((one) => recentSites.indexOf(one.site) < 0).map((one) => `${one.site}(${one.clause})`);
      const still = sitesOf(list).filter((site) => recentSites.indexOf(site) >= 0).map((site) => `${site}(${refName}에도 기재)`);
      return `${monthsText([when])} 메모 이상 ${sitesOf(list).length}개 현장: ${[...only, ...still].join(', ')}`;
    }),
  ];
  return {
    summary: `판정 항목은 없으나 완료 점검 메모에 ${label} 기재 확인 필요(${head}${findings.length > 1 ? ' 등' : ''})`,
    checks,
    note: '메모(비고) 문구 기준, 판정 항목 아님',
    ...(parts.length ? {
      action: `${parts.join('과 ')} 확인(${dayLabel(nextWorkday(basis))}까지${owners.length
        ? `, ${tight && owners.length > 1 ? `${owners[0]} 외 ${owners.length - 1}명` : fewNames(owners)}` : ''})`,
    } : {}),
    facts,
  };
}
